// API client for Code Concierge

import { resolveApiBase as resolveApiBaseForPath, withMountBase } from './mountBase'

export function resolveApiBase(pathname?: string): string {
  return resolveApiBaseForPath(pathname)
}

const API_BASE = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')

export interface User {
  id: number
  email: string
  name: string | null
  first_name?: string | null
  last_name?: string | null
  is_admin: boolean
  is_active: boolean
  created_at: string
  last_login: string | null
}

export interface AdminUserUpdate {
  name?: string | null
  is_admin?: boolean
  is_active?: boolean
  settings?: Record<string, unknown>
}

export interface App {
  id: number
  name: string
  display_name: string | null
  description: string | null
  port: number
  status: string  // Settled state: 'running', 'stopped', 'starting', 'error'
  deployment_phase?: string  // Granular phase: 'stopped', 'queued', 'building', 'installing', 'starting', 'running', 'error'
  error_message?: string | null  // Human-readable error text
  error_details?: { log_excerpt?: string; port?: number; resolution?: string } | null
  runtime: string  // 'node', 'python', 'static'
  access_level: string  // 'personal', 'specific', 'platform', 'public', 'link'
  git_initialized: boolean
  url: string
  workspace_url: string
  created_at: string
  updated_at: string | null
  last_accessed: string | null
  keep_alive: boolean
  rate_limit_exempt?: boolean
  category?: string | null  // admin-managed category slug
  // Visit metrics (populated on list endpoints for platform-shared apps)
  visit_count_30d?: number
  unique_visitors_30d?: number
  // Permission info (included when viewing a specific app)
  is_owner?: boolean
  my_permission?: string  // 'owner', 'admin', 'edit', 'view', 'platform'
  // Production build fields
  production_status?: string | null  // 'building', 'running', 'stopped', 'error', null
  production_error_message?: string | null
  last_successful_build?: string | null  // ISO timestamp
  production_url?: string | null  // URL to production version
  dev_preview_url?: string | null  // URL to dev preview (when production exists)
  // Featured and favorited status (populated on list endpoints)
  is_featured?: boolean
  is_favorited?: boolean
  // Workspace breadcrumb switcher pin: ordering anchor at the top of the
  // switcher dropdown. pinned_rank is 0 for the most recently pinned app.
  is_pinned?: boolean
  pinned_rank?: number | null
  // Context fields (populated by specific endpoints)
  owner_email?: string
  owner_name?: string | null
  has_pending_request?: boolean
  last_visited_at?: string | null
  deleted_at?: string | null
  restore_deadline?: string | null
  access_denied?: boolean
  // Template fields
  is_template?: boolean
  template_active?: boolean
  template_one_liner?: string | null
  template_description?: string | null
  template_hero_image?: string | null
  source_template_id?: number | null
  from_template?: boolean
}

export interface DeletedApp extends App {
  deleted_at: string
  restore_deadline: string
}

export interface CloneAppResponse extends App {
  is_own_clone: boolean
  secret_copy_status?: 'copied' | 'not_copied' | 'failed'
}

export interface AdminApp extends App {
  user_id: number
  user_email: string
  user_name: string | null
  is_featured?: boolean
  featured_at?: string | null
  inventory_source?: string
  live_status?: string | null
  live_health?: string | null
  live_container_count?: number
  live_container_names?: string[]
  observed_at?: string | null
}

export interface AppShare {
  id: number
  email: string
  permission: string  // 'view', 'edit', 'admin'
  user_registered: boolean
  accepted: boolean
  invited_at: string
}

export interface NameCheckResult {
  name: string
  available: boolean
  suggestion: string | null
}

export interface Category {
  id: number
  slug: string
  name: string
  color: string
  display_order: number
}

export interface UserSettings {
  theme: string
  editor_font_size: number
  terminal_font_size: number
  vnc_resolution: string
  auto_save: boolean
  claude_settings: object
}

interface ApiValidationError {
  field: string
  message: string
}

interface ApiErrorDetailObject {
  message?: string
  validation_errors?: ApiValidationError[]
}

interface ApiErrorEnvelope {
  detail?: string | ApiErrorDetailObject
  error?: string | {
    code?: string
    message?: string
    details?: Record<string, unknown>
  }
}

export class ApiRequestError extends Error {
  status: number
  code?: string
  details?: Record<string, unknown>
  retryAfterSeconds?: number

  constructor(
    message: string,
    options: {
      status: number
      code?: string
      details?: Record<string, unknown>
      retryAfterSeconds?: number
    }
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = options.status
    this.code = options.code
    this.details = options.details
    this.retryAfterSeconds = options.retryAfterSeconds
  }
}

function toObjectRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? value as Record<string, unknown> : undefined
}

function parseRetryAfterHeader(value: string | null): number | undefined {
  if (!value) return undefined

  const asSeconds = Number(value)
  if (Number.isFinite(asSeconds) && asSeconds >= 0) {
    return Math.ceil(asSeconds)
  }

  const asDate = Date.parse(value)
  if (Number.isNaN(asDate)) return undefined
  return Math.max(0, Math.ceil((asDate - Date.now()) / 1000))
}

function buildApiRequestError(
  response: Response,
  payload: unknown,
  fallbackMessage = 'Request failed'
): ApiRequestError {
  let message = fallbackMessage
  let code: string | undefined
  let details: Record<string, unknown> | undefined

  const retryAfterSeconds = parseRetryAfterHeader(response.headers.get('Retry-After'))

  if (typeof payload === 'string' && payload.trim()) {
    message = payload
  } else {
    const error = (payload && typeof payload === 'object'
      ? payload
      : { detail: fallbackMessage }) as ApiErrorEnvelope

    const detail = error.detail
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object' && detail.message) {
      message = detail.message
      details = { ...detail }
      if (detail.validation_errors?.length) {
        const fields = detail.validation_errors.map((e) => `${e.field}: ${e.message}`).join('; ')
        message = `${detail.message}: ${fields}`
      }
    }

    if (typeof error.error === 'string') {
      message = error.error
    } else {
      const errorObject = toObjectRecord(error.error)
      if (typeof errorObject?.code === 'string') {
        code = errorObject.code
      }
      if (typeof errorObject?.message === 'string' && errorObject.message.trim()) {
        message = errorObject.message
      }
      const errorDetails = toObjectRecord(errorObject?.details)
      if (errorDetails) {
        details = { ...(details || {}), ...errorDetails }
      }
    }
  }

  if (retryAfterSeconds !== undefined) {
    details = { ...(details || {}), retry_after_seconds: retryAfterSeconds }
  }

  return new ApiRequestError(message, {
    status: response.status,
    code,
    details,
    retryAfterSeconds,
  })
}

async function throwApiError(response: Response, fallbackMessage = 'Request failed'): Promise<never> {
  const contentType = response.headers.get('content-type') || ''
  const isJsonResponse = contentType.toLowerCase().includes('application/json')
  const responseText = await response.text()

  let payload: unknown = responseText
  if (isJsonResponse) {
    try {
      payload = responseText ? JSON.parse(responseText) : null
    } catch {
      payload = responseText
    }
  }

  throw buildApiRequestError(response, payload, fallbackMessage)
}

function withJsonHeaders(options?: RequestInit): RequestInit {
  return {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  }
}

async function fetchJsonDirect<T>(
  input: RequestInfo | URL,
  options?: RequestInit,
  fallbackMessage = 'Request failed'
): Promise<T> {
  const response = await fetch(input, withJsonHeaders(options))
  if (!response.ok) {
    await throwApiError(response, fallbackMessage)
  }
  return response.json()
}

async function fetchJsonDirectOrDefault<T>(
  input: RequestInfo | URL,
  defaultValue: T,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(input, withJsonHeaders(options))
  if (response.ok) {
    return response.json()
  }
  if (response.status === 401 || response.status === 403) {
    return defaultValue
  }
  await throwApiError(response, 'Request failed')
  throw new Error('unreachable')
}

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  const contentType = response.headers.get('content-type') || ''
  const isJsonResponse = contentType.toLowerCase().includes('application/json')
  const responseText = await response.text()

  // Some endpoints intentionally return 204 No Content.
  if (response.ok && (response.status === 204 || !responseText.trim())) {
    return null as T
  }

  // Some auth failures/proxy redirects return HTML (login page) with 200 status.
  // Avoid raw JSON parse errors like "Unexpected token '<'".
  if (!isJsonResponse) {
    const looksLikeHtml = responseText.trim().toLowerCase().startsWith('<!doctype html')
      || responseText.trim().startsWith('<html')
      || contentType.toLowerCase().includes('text/html')
      || response.url.includes('/login')
      || response.redirected
    if (looksLikeHtml) {
      throw new Error('Session expired or unauthorized. Please refresh and sign in again.')
    }
    const preview = responseText.trim().slice(0, 200)
    const body = preview ? `: ${preview}` : ''
    throw new Error(`Unexpected API response (${contentType || 'unknown'}, status ${response.status})${body}`)
  }

  let payload: unknown = null
  try {
    payload = responseText ? JSON.parse(responseText) : null
  } catch {
    throw new Error('Invalid JSON response from server')
  }

  if (!response.ok) {
    throw buildApiRequestError(response, payload, 'Request failed')
  }

  return payload as T
}

// Current user
export async function getCurrentUser(): Promise<{
  email: string
  name: string
  is_admin: boolean
  first_name?: string
  last_name?: string
  services?: string[]
  features?: {
    templates_visible_to_users?: boolean
    templates_visible_to_me?: boolean
  }
}> {
  return fetchAPI('/me')
}

// Users (admin)
export async function listUsers(search?: string): Promise<User[]> {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  return fetchAPI(`/users${params}`)
}

export async function createUser(data: Partial<User>): Promise<User> {
  return fetchAPI('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateUser(id: number, data: AdminUserUpdate): Promise<User> {
  return fetchAPI(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteUser(id: number): Promise<void> {
  return fetchAPI(`/users/${id}`, { method: 'DELETE' })
}

// Apps (user) - Frictionless App Management
export async function listMyApps(): Promise<App[]> {
  return fetchAPI('/apps')
}

export async function listDeletedApps(): Promise<DeletedApp[]> {
  return fetchAPI('/apps/deleted')
}

export async function getApp(appName: string): Promise<App> {
  return fetchAPI(`/apps/${appName}`)
}

export async function sendWorkspaceFocusHeartbeat(appName: string): Promise<{ ok: boolean }> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/focus-heartbeat`, {
    method: 'POST',
  })
}

// Fires once when the Workspace.tsx page mounts for an app. The server
// debounces per (user, app) to avoid spamming audit_log on hot-reload.
export async function trackWorkspaceOpen(
  appName: string,
): Promise<{ ok: boolean; logged: boolean }> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/track-workspace-open`, {
    method: 'POST',
  })
}

export async function createApp(data: {
  name: string
  display_name?: string
  description?: string
  runtime?: string
  access_level?: string
  auto_build?: boolean
}): Promise<App> {
  return fetchAPI('/apps', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateApp(name: string, data: Partial<App>): Promise<App> {
  return fetchAPI(`/apps/${name}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteApp(name: string): Promise<DeletedApp> {
  return fetchAPI(`/apps/${name}`, { method: 'DELETE' })
}

export async function restoreApp(name: string): Promise<App> {
  return fetchAPI(`/apps/${name}/restore`, { method: 'POST' })
}

export async function permanentlyDeleteApp(name: string): Promise<void> {
  return fetchAPI(`/apps/${name}/permanent`, { method: 'DELETE' })
}

export async function cloneApp(
  appName: string,
  data: { name: string; display_name?: string }
): Promise<CloneAppResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/clone`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function checkAppName(name: string, exclude?: string): Promise<NameCheckResult> {
  const params = exclude ? `?exclude=${encodeURIComponent(exclude)}` : ''
  return fetchAPI(`/apps/check-name/${encodeURIComponent(name)}${params}`)
}

export async function startApp(name: string): Promise<{ status: string; name: string; deployment_phase?: string }> {
  return fetchAPI(`/apps/${name}/start`, { method: 'POST' })
}

export async function stopApp(name: string): Promise<{ status: string; name: string }> {
  return fetchAPI(`/apps/${name}/stop`, { method: 'POST' })
}

export async function restartApp(name: string): Promise<{ status: string; name: string }> {
  return fetchAPI(`/apps/${name}/restart`, { method: 'POST' })
}

export async function rebuildApp(name: string): Promise<{ status: string; name: string; deployment_phase?: string }> {
  return fetchAPI(`/apps/${name}/rebuild`, { method: 'POST' })
}

export async function getAppLogs(
  name: string,
  lines?: number,
  logType?: 'all' | 'build' | 'runtime'
): Promise<{ name: string; logs: string; lines: number; deployment_phase?: string; log_type?: string }> {
  const params = new URLSearchParams()
  if (lines) params.append('lines', lines.toString())
  if (logType && logType !== 'all') params.append('type', logType)
  const qs = params.toString()
  return fetchAPI(`/apps/${name}/logs${qs ? `?${qs}` : ''}`)
}

// Deployment Status
export interface DeploymentStatus {
  status: string
  deployment_phase: string
  error_message: string | null
  error_details: { log_excerpt?: string; port?: number; resolution?: string } | null
  // Production build fields
  production_status?: string | null
  production_error_message?: string | null
  last_successful_build?: string | null
}

export async function getDeploymentStatus(appName: string): Promise<DeploymentStatus> {
  return fetchAPI(`/apps/${appName}/deployment-status`)
}

// App Secrets
export interface AppSecretMetadata {
  name: string
  description: string | null
  created_at: string | null
  updated_at: string | null
  last_used_at: string | null
  version: number | null
  is_set: boolean
}

export interface RequiredAppSecret {
  name: string
  display_name?: string | null
  description?: string | null
  kind: 'env' | 'file' | string
  mount_path?: string | null
  env_names?: string[]
  service_names?: string[]
  referenced_by: string[]
  source?: string | null
  is_set: boolean
}

export interface AppSecretsResponse {
  app_name: string
  secrets: AppSecretMetadata[]
  required_secrets: RequiredAppSecret[]
}

export async function listAppSecrets(appName: string): Promise<AppSecretsResponse> {
  return fetchAPI(`/deployed-apps/${appName}/secrets`)
}

export async function upsertAppSecret(
  appName: string,
  key: string,
  data: { value: string | null; description?: string; display_name?: string }
): Promise<{ status: string; secret: AppSecretMetadata }> {
  // value=null clears the value but keeps the catalog row.
  return fetchAPI(`/deployed-apps/${appName}/secrets/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteAppSecret(
  appName: string,
  key: string
): Promise<{ status: string; name: string }> {
  return fetchAPI(`/deployed-apps/${appName}/secrets/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  })
}

export interface SecretScanFinding {
  id: number
  app_id: number
  app_name: string
  severity: 'high' | 'medium' | 'low' | string
  status: 'open' | 'resolved' | 'ignored' | string
  rule_id: string
  file_path: string
  line_number: number | null
  excerpt: string
  hint: string | null
  admin_note?: string | null
  status_updated_by?: number | null
  source: string | null
  created_at: string | null
}

export interface SecretFindingsResponse {
  findings: SecretScanFinding[]
  total: number
  limit: number
  offset: number
  severity: string | null
  status: string | null
  app_name: string | null
}

export async function listSecretFindings(params?: {
  severity?: 'high' | 'medium' | 'low'
  status?: 'open' | 'resolved' | 'ignored'
  app_name?: string
  limit?: number
  offset?: number
}): Promise<SecretFindingsResponse> {
  const qp = new URLSearchParams()
  if (params?.severity) qp.set('severity', params.severity)
  if (params?.status) qp.set('status', params.status)
  if (params?.app_name) qp.set('app_name', params.app_name)
  if (params?.limit != null) qp.set('limit', String(params.limit))
  if (params?.offset != null) qp.set('offset', String(params.offset))
  const query = qp.toString()
  return fetchAPI(`/admin/secret-findings${query ? `?${query}` : ''}`)
}

export async function updateSecretFindingStatus(
  findingId: number,
  data: { status: 'open' | 'resolved' | 'ignored'; note?: string }
): Promise<{ status: string; finding: SecretScanFinding }> {
  return fetchAPI(`/admin/secret-findings/${findingId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function rescanAppSecretFindings(
  appName: string
): Promise<{
  status: string
  app_name: string
  decision: string
  message: string
  summary: {
    total_findings: number
    counts_by_severity: { high: number; medium: number; low: number }
  }
}> {
  return fetchAPI(`/admin/secret-findings/rescan/${encodeURIComponent(appName)}`, {
    method: 'POST',
  })
}

// Admin: Notification test-fire (operational diagnostics)
export interface AdminNotificationType {
  type: string
  priority: 'high' | 'medium' | 'low'
}

export async function listAdminNotificationTypes(): Promise<{ types: AdminNotificationType[] }> {
  return fetchAPI('/admin/notifications/types')
}

export async function fireTestNotification(input: {
  notification_type: string
  target_user_email: string
  app_name?: string
}): Promise<{ status: string; notification_type: string; target_user_email: string }> {
  return fetchAPI('/admin/notifications/test-fire', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

// Production Build
export async function triggerProductionBuild(appName: string): Promise<{ status: string; message: string }> {
  return fetchAPI(`/apps/${appName}/production-build`, { method: 'POST' })
}

export async function stopProductionBuild(appName: string): Promise<{ status: string }> {
  return fetchAPI(`/apps/${appName}/production-stop`, { method: 'POST' })
}

export async function getProductionLogs(appName: string): Promise<{ build_logs: string; runtime_logs: string; production_status: string }> {
  return fetchAPI(`/apps/${appName}/production-logs`)
}

// App Sharing
export async function listAppShares(name: string): Promise<AppShare[]> {
  return fetchAPI(`/apps/${name}/shares`)
}

export async function shareApp(name: string, email: string, permission?: string): Promise<AppShare> {
  return fetchAPI(`/apps/${name}/share`, {
    method: 'POST',
    body: JSON.stringify({ email, permission: permission || 'view' }),
  })
}

export async function removeAppShare(name: string, email: string): Promise<void> {
  return fetchAPI(`/apps/${name}/share/${encodeURIComponent(email)}`, { method: 'DELETE' })
}

// Shared Apps (apps shared with the current user, not owned)
export async function listSharedApps(): Promise<App[]> {
  return fetchAPI('/apps/shared-with-me')
}

// Recipient-initiated unsubscribe (SHARE-01)
export async function unsubscribeFromShare(name: string): Promise<{ status: string; app_name: string }> {
  return fetchAPI(`/apps/${name}/shares/mine`, { method: 'DELETE' })
}

// Popular Apps (platform-shared apps with high visitor counts)
export async function listPopularApps(limit: number = 20): Promise<App[]> {
  return fetchAPI(`/apps/popular?limit=${limit}`)
}

// Recently Viewed Apps (apps the user has visited, from Redis sorted set)
export async function listRecentlyViewedApps(limit: number = 10): Promise<App[]> {
  return fetchAPI(`/apps/recently-viewed?limit=${limit}`)
}

// Favorites
export async function listFavoriteApps(): Promise<App[]> {
  return fetchAPI('/apps/favorites')
}

export async function favoriteApp(name: string): Promise<void> {
  return fetchAPI(`/apps/${name}/favorite`, { method: 'POST' })
}

export async function unfavoriteApp(name: string): Promise<void> {
  return fetchAPI(`/apps/${name}/favorite`, { method: 'DELETE' })
}

export async function pinApp(name: string): Promise<{ status: string; pinned_rank: number }> {
  return fetchAPI(`/apps/${encodeURIComponent(name)}/pin`, { method: 'POST' })
}

export async function unpinApp(name: string): Promise<{ status: string; noop?: boolean }> {
  return fetchAPI(`/apps/${encodeURIComponent(name)}/pin`, { method: 'DELETE' })
}

// Browse & Access Requests
export async function browseApps(): Promise<App[]> {
  return fetchAPI('/apps/browse')
}

export async function requestAccess(name: string): Promise<{ status: string }> {
  return fetchAPI(`/apps/${name}/request-access`, { method: 'POST' })
}

// Access Request Management (for app owners)
export interface AccessRequest {
  id: number
  app_name: string
  app_display_name: string | null
  requester_email: string
  requester_name: string | null
  status: string
  created_at: string
}

export async function listAccessRequests(): Promise<AccessRequest[]> {
  return fetchAPI('/apps/access-requests')
}

export async function approveAccessRequest(requestId: number): Promise<{ status: string }> {
  return fetchAPI(`/apps/access-requests/${requestId}/approve`, { method: 'POST' })
}

export async function denyAccessRequest(requestId: number): Promise<{ status: string }> {
  return fetchAPI(`/apps/access-requests/${requestId}/deny`, { method: 'POST' })
}

// Per-user share permission update
export async function updateSharePermission(
  appName: string, email: string, permission: string
): Promise<{ status: string; email: string; permission: string }> {
  return fetchAPI(`/apps/${appName}/share/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: JSON.stringify({ permission }),
  })
}

// Public-Access Requests (admin-gated)
export interface PublicAccessRequestStatus {
  id: number
  status: 'pending' | 'approved' | 'rejected'
  justification: string | null
  review_note: string | null
  created_at: string
  reviewed_at: string | null
}

export async function requestPublicAccess(
  appName: string,
  justification?: string,
): Promise<{ status: string; request_id?: number; access_level?: string }> {
  return fetchAPI(`/apps/${appName}/request-public-access`, {
    method: 'POST',
    body: JSON.stringify({ justification: justification ?? null }),
  })
}

export async function getPublicAccessRequestStatus(
  appName: string,
): Promise<{ request: PublicAccessRequestStatus | null }> {
  return fetchAPI(`/apps/${appName}/public-access-request`)
}

export async function approvePublicAccessRequest(
  requestId: number,
  reviewNote?: string,
): Promise<{ status: string; request_id: number; app_name: string }> {
  return fetchAPI(`/admin/public-access-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ review_note: reviewNote ?? null }),
  })
}

export async function rejectPublicAccessRequest(
  requestId: number,
  reviewNote?: string,
): Promise<{ status: string; request_id: number; app_name: string }> {
  return fetchAPI(`/admin/public-access-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ review_note: reviewNote ?? null }),
  })
}

// Platform Sharing
export async function shareWithPlatform(name: string): Promise<{ status: string; access_level: string }> {
  return fetchAPI(`/apps/${name}/share-with-platform`, { method: 'POST' })
}

export async function unshareFromPlatform(name: string): Promise<{ status: string; access_level: string }> {
  return fetchAPI(`/apps/${name}/share-with-platform`, { method: 'DELETE' })
}

function buildAbsolutePlatformUrl(path: string): string {
  const mountedPath = withMountBase(path, typeof window !== 'undefined' ? window.location.pathname : '/')
  if (typeof window === 'undefined') {
    return mountedPath
  }
  return `${window.location.origin}${mountedPath}`
}

function syntheticAdminAppId(name: string): number {
  let checksum = 0
  for (let index = 0; index < name.length; index += 1) {
    checksum = ((checksum * 33) + name.charCodeAt(index)) >>> 0
  }
  return -Math.max(1, checksum)
}

interface LiveAdminAppAggregate {
  name: string
  user_email: string | null
  statuses: Set<string>
  healths: Set<string>
  container_names: Set<string>
  observed_at: string | null
}

function deriveLiveAdminAppStatus(statuses: Set<string>, healths: Set<string>): string {
  if (healths.has('unhealthy')) return 'error'
  if (statuses.has('running')) return 'running'
  if (statuses.has('created') || statuses.has('restarting') || statuses.has('paused')) return 'starting'
  if (statuses.size > 0) return 'stopped'
  return 'unknown'
}

function deriveLiveAdminAppHealth(healths: Set<string>): string | null {
  if (healths.has('unhealthy')) return 'unhealthy'
  if (healths.has('healthy')) return 'healthy'
  if (healths.has('starting')) return 'starting'
  if (healths.has('none')) return 'none'
  return healths.values().next().value ?? null
}

function collectLiveAdminApps(
  snapshot: SystemObservabilitySnapshot,
  userEmailFilter?: string | null
): Map<string, LiveAdminAppAggregate> {
  const filterEmail = userEmailFilter?.toLowerCase()
  const liveApps = new Map<string, LiveAdminAppAggregate>()

  snapshot.containers.items.forEach((item) => {
    const appName = item.labels.app_name?.trim()
    if (!appName || item.kind !== 'app') return

    const userEmail = item.labels.user_email?.trim().toLowerCase() ?? null
    if (filterEmail && userEmail !== filterEmail) return

    const existing = liveApps.get(appName) ?? {
      name: appName,
      user_email: userEmail,
      statuses: new Set<string>(),
      healths: new Set<string>(),
      container_names: new Set<string>(),
      observed_at: null,
    }

    existing.user_email = existing.user_email ?? userEmail
    existing.statuses.add(item.status)
    existing.healths.add(item.health)
    existing.container_names.add(item.name)

    const observedAt = item.started_at ?? item.created_at ?? item.finished_at ?? null
    if (observedAt && (!existing.observed_at || observedAt > existing.observed_at)) {
      existing.observed_at = observedAt
    }

    liveApps.set(appName, existing)
  })

  return liveApps
}

function mergeAdminAppsWithLiveInventory(
  dbApps: AdminApp[],
  snapshot: SystemObservabilitySnapshot,
  userEmailFilter?: string | null
): AdminApp[] {
  const merged = new Map<string, AdminApp>()
  const liveApps = collectLiveAdminApps(snapshot, userEmailFilter)

  dbApps.forEach((app) => {
    merged.set(app.name, app)
  })

  liveApps.forEach((liveApp, appName) => {
    const liveStatus = deriveLiveAdminAppStatus(liveApp.statuses, liveApp.healths)
    const liveHealth = deriveLiveAdminAppHealth(liveApp.healths)
    const liveContainerNames = Array.from(liveApp.container_names).sort()
    const existing = merged.get(appName)

    if (existing) {
      const hasDbRecord = existing.inventory_source !== 'live_only' && existing.id > 0
      merged.set(appName, {
        ...existing,
        status: liveStatus,
        inventory_source: hasDbRecord ? 'merged' : 'live_only',
        live_status: liveStatus,
        live_health: liveHealth,
        live_container_count: liveContainerNames.length,
        live_container_names: liveContainerNames,
        observed_at: liveApp.observed_at ?? existing.observed_at ?? null,
      })
      return
    }

    const observedAt = liveApp.observed_at ?? new Date().toISOString()
    merged.set(appName, {
      id: syntheticAdminAppId(appName),
      name: appName,
      display_name: null,
      description: 'Observed via live container inventory; no deployed_apps row exists.',
      port: 0,
      status: liveStatus,
      runtime: 'unknown',
      access_level: 'unknown',
      git_initialized: false,
      url: buildAbsolutePlatformUrl(`/a/${appName}/`),
      workspace_url: buildAbsolutePlatformUrl(`/workspace/${appName}/`),
      created_at: observedAt,
      updated_at: observedAt,
      last_accessed: observedAt,
      keep_alive: false,
      user_id: 0,
      user_email: liveApp.user_email ?? 'unknown',
      user_name: null,
      visit_count_30d: 0,
      unique_visitors_30d: 0,
      is_featured: false,
      featured_at: null,
      inventory_source: 'live_only',
      live_status: liveStatus,
      live_health: liveHealth,
      live_container_count: liveContainerNames.length,
      live_container_names: liveContainerNames,
      observed_at: observedAt,
    })
  })

  return Array.from(merged.values()).sort((left, right) => (right.created_at ?? '').localeCompare(left.created_at ?? ''))
}

// Apps (admin)
export async function listAllApps(userId?: number): Promise<AdminApp[]> {
  const params = userId ? `?user_id=${userId}` : ''
  const [dbApps, snapshot, userDetail] = await Promise.all([
    fetchAPI(`/apps/admin/all${params}`) as Promise<AdminApp[]>,
    getSystemObservability({ include_stopped: true, limit: 1000, history_points: 10 }).catch(() => null),
    userId
      ? fetchAPI<{ email: string }>(`/admin/users/${userId}`).catch(() => null)
      : Promise.resolve(null),
  ])

  if (!snapshot) {
    return dbApps
  }

  const userEmailFilter = typeof userDetail?.email === 'string' ? userDetail.email : null
  return mergeAdminAppsWithLiveInventory(dbApps, snapshot, userEmailFilter)
}

export async function toggleFeaturedApp(name: string): Promise<{ status: string; is_featured: boolean }> {
  return fetchAPI(`/apps/admin/${name}/toggle-featured`, { method: 'POST' })
}

// Categories
export async function listCategories(): Promise<Category[]> {
  return fetchAPI('/categories')
}

export async function createCategory(data: { slug: string; name: string; color?: string; display_order?: number }): Promise<Category> {
  return fetchAPI('/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateCategory(slug: string, data: { name?: string; color?: string; display_order?: number }): Promise<Category> {
  return fetchAPI(`/categories/${slug}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteCategory(slug: string): Promise<void> {
  return fetchAPI(`/categories/${slug}`, { method: 'DELETE' })
}

// Settings (user)
export async function getSettings(): Promise<UserSettings> {
  return fetchAPI('/settings')
}

export async function updateSettings(data: Partial<UserSettings>): Promise<UserSettings> {
  return fetchAPI('/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function resetSettings(): Promise<UserSettings> {
  return fetchAPI('/settings/reset', { method: 'POST' })
}

// Workspace (router service)
export interface Workspace {
  container_id: string
  container_name: string
  email: string
  created: string
  endpoints: {
    code: string
    terminal: string
    desktop: string
  }
}

// Admin Workspace (with user info and health)
export interface AdminWorkspace {
  id: number
  user_id: number
  container_id: string
  container_name: string
  status: string
  live_status: string
  docker_health?: string
  created_at: string | null
  updated_at: string | null
  last_accessed: string | null
  health_status: string | null
  last_health_check: string | null
  health_check_failures: number | null
  health_message: string | null
  email: string
  user_name: string | null
}

export async function ensureWorkspace(): Promise<Workspace> {
  // Use /workspace/self to avoid Traefik routing ambiguity where bare
  // /api/workspace can match the general /api catch-all instead of the router
  return fetchJsonDirect(`${API_BASE}/workspace/self`, undefined, 'Failed to initialize workspace')
}

// App Containers (workspace tools)
export interface AppContainer {
  id: number
  workspace_id: number
  app_type: string  // 'terminal', 'browser', 'vscode', 'claude'
  container_id: string | null
  container_name: string
  port: number
  status: string  // 'running', 'stopped', 'starting', 'error'
  docker_status?: string  // live status from Docker
  created_at: string | null
  updated_at: string | null
  last_accessed: string | null
}

export interface AppContainersResponse {
  app_containers: AppContainer[]
  message?: string
}

export async function listAppContainers(): Promise<AppContainersResponse> {
  return fetchJsonDirect(`${API_BASE}/workspace/apps`, undefined, 'Failed to list app containers')
}

export async function restartAppContainer(appType: string): Promise<{ status: string; container: string }> {
  return fetchJsonDirect(
    `${API_BASE}/workspace/apps/${appType}/restart`,
    { method: 'POST' },
    'Failed to restart container'
  )
}

// Per-App Workspace Container Management
export interface AppWorkspaceContainerStatus {
  container: string
  status: string
  created?: string
  started?: string
}

export async function getAppWorkspaceContainerStatus(appName: string): Promise<AppWorkspaceContainerStatus> {
  return fetchAPI(`/workspace/app/${appName}/container/status`)
}

export async function restartAppWorkspaceContainer(appName: string): Promise<{ status: string; container: string }> {
  return fetchAPI(`/workspace/app/${appName}/restart`, { method: 'POST' })
}

// Removes the workspace container but preserves the app's filesystem
// (workspace files, .git, .claude, etc.). Use this to force a fresh
// container that pulls the latest image and re-injects platform env vars.
// Filesystem-wiping container deletion no longer exists as an endpoint —
// it is scoped to the full app-deletion flow only.
export async function recreateAppWorkspaceContainer(appName: string): Promise<{ status: string; container: string }> {
  return fetchAPI(`/workspace/app/${appName}/container-only`, { method: 'DELETE' })
}

// Admin Workspaces
export async function listWorkspaces(): Promise<AdminWorkspace[]> {
  return fetchAPI('/admin/workspaces')
}

export async function restartWorkspace(containerName: string): Promise<{ status: string; container: string }> {
  return fetchAPI(`/admin/workspaces/${containerName}/restart`, { method: 'POST' })
}

export async function stopWorkspace(containerName: string): Promise<{ status: string; container: string }> {
  return fetchAPI(`/admin/workspaces/${containerName}/stop`, { method: 'POST' })
}

export async function startWorkspace(containerName: string): Promise<{ status: string; container: string }> {
  return fetchAPI(`/admin/workspaces/${containerName}/start`, { method: 'POST' })
}

export interface SystemObservabilityContainer {
  name: string
  id: string
  kind: string
  status: string
  health: string
  created_at?: string
  started_at?: string
  finished_at?: string
  restart_count: number
  metrics_sampled: boolean
  metrics_fresh?: boolean
  stats_age_seconds?: number | null
  stats_source?: string | null
  cpu_percent: number
  memory_usage_bytes: number
  memory_limit_bytes: number
  memory_percent: number
  network_rx_bytes: number
  network_tx_bytes: number
  labels: {
    platform?: string
    app_name?: string
    user_email?: string
    deployment?: string
    user_id?: string
    workspace_name?: string
    [key: string]: string | undefined
  }
}

export interface WebSocketObservabilityEvent {
  timestamp: string
  event: string
  path?: string
  target_host?: string
  target_port?: number
  reason?: string
  error?: string
  initiator?: string
  client_close_code?: number | string | null
  backend_close_code?: number | string | null
  client_event?: string | null
  backend_event?: string | null
}

export interface HttpProxyObservabilityEvent {
  timestamp: string
  event: string
  path?: string
  target_host?: string
  target_port?: number
  elapsed_ms?: number
  status_code?: number | null
  error_kind?: string | null
}

export interface SystemObservabilityHistoryPoint {
  timestamp: string
  running_containers: number
  unhealthy_containers: number
  cpu_percent_sum: number
  memory_percent: number
  websocket_active: number
  websocket_connect_failures_total: number
  websocket_closed_connections: number
  http_proxy_total_requests: number
  http_proxy_transport_errors_total: number
  http_proxy_timeout_errors_total: number
  http_proxy_p50_latency_ms: number
  http_proxy_p95_latency_ms: number
  client_error_events_total: number
  client_terminal_ws_errors_total: number
  client_terminal_recovery_events_total: number
  client_iframe_load_errors_total: number
  client_browser_service_start_failures_total: number
  client_unhandled_errors_total: number
}

export interface ClientObservabilityEvent {
  timestamp: string
  event_type: string
  panel: string
  severity: string
  path?: string
  app_name?: string
  user_email?: string
  message?: string
  details?: Record<string, unknown>
}

export interface SystemObservabilitySnapshot {
  generated_at: string
  collection: {
    cache_age_seconds: number
    cache_ttl_seconds: number
    last_refresh_ms: number
    last_refresh_error?: string | null
    generated_age_seconds?: number
    stale_threshold_seconds?: number
    is_stale?: boolean
    freshness_state?: string
    worker_count?: number
  }
  system_health: {
    http_proxy_timeout_errors_total: number
    http_proxy_transport_errors_total: number
    websocket_connect_failures_total: number
    client_error_events_total: number
    client_terminal_ws_errors_total: number
    client_terminal_recovery_events_total: number
    client_iframe_load_errors_total: number
    client_browser_service_start_failures_total: number
    client_unhandled_errors_total: number
  }
  containers: {
    total: number
    returned: number
    truncated: boolean
    running: number
    unhealthy: number
    status_counts: Record<string, number>
    kind_counts: Record<string, number>
    resource_collection: {
      stats_budget_seconds: number
      stats_max_samples: number
      sampled_running_containers: number
      failed_running_containers: number
      skipped_running_containers: number
      live_running_containers?: number
      stale_running_containers?: number
      missing_running_containers?: number
      stats_timeout_seconds?: number
      stats_concurrency?: number
      sampling_coverage_ratio?: number
      resource_totals_scope?: string
    }
    resource_totals: {
      cpu_percent_sum: number
      memory_usage_bytes: number
      memory_limit_bytes: number
      memory_percent: number
      network_rx_bytes: number
      network_tx_bytes: number
    }
    items: SystemObservabilityContainer[]
  }
  websocket: {
    attempted_connections: number
    accepted_connections: number
    active_connections: number
    connect_failures_total: number
    closed_connections: number
    connect_failures_by_reason: Record<string, number>
    close_initiator_counts: Record<string, number>
    client_close_code_counts: Record<string, number>
    backend_close_code_counts: Record<string, number>
    recent_events: WebSocketObservabilityEvent[]
  }
  http_proxy: {
    total_requests: number
    active_requests: number
    responses_total: number
    transport_errors_total: number
    timeout_errors_total: number
    connect_errors_total: number
    status_code_counts: Record<string, number>
    latency_ms: {
      samples: number
      avg: number
      p50: number
      p95: number
      max: number
    }
    recent_events: HttpProxyObservabilityEvent[]
  }
  client: {
    events_total: number
    severity_counts: Record<string, number>
    events_by_type: Record<string, number>
    events_by_panel: Record<string, number>
    terminal_ws_errors_total: number
    terminal_recovery_events_total: number
    iframe_load_errors_total: number
    browser_service_start_failures_total: number
    unhandled_errors_total: number
    recent_events: ClientObservabilityEvent[]
  }
  history: SystemObservabilityHistoryPoint[]
}

export interface OpsMetricSnapshot {
  metric_name: string
  metric_value: number
  dimensions: Record<string, unknown>
  computed_at: string
}

export interface OpsAlertRecord {
  alert_key: string
  severity: string
  title?: string | null
  active: boolean
  metric_value: number
  dimensions: Record<string, unknown>
  computed_at: string
}

export async function getSystemObservability(params?: {
  include_stopped?: boolean
  limit?: number
  history_points?: number
}): Promise<SystemObservabilitySnapshot> {
  const searchParams = new URLSearchParams()
  if (params?.include_stopped !== undefined) searchParams.append('include_stopped', String(params.include_stopped))
  if (params?.limit) searchParams.append('limit', String(params.limit))
  if (params?.history_points) searchParams.append('history_points', String(params.history_points))
  const query = searchParams.toString()
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 6000)
  try {
    return await fetchAPI(`/admin/observability/system${query ? `?${query}` : ''}`, {
      signal: controller.signal,
    })
  } catch (error) {
    if (
      error instanceof DOMException
      && error.name === 'AbortError'
    ) {
      throw new Error('System observability request timed out after 6s')
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function getOpsAlerts(params?: {
  include_inactive?: boolean
  limit?: number
}): Promise<OpsAlertRecord[]> {
  const searchParams = new URLSearchParams()
  if (params?.include_inactive !== undefined) searchParams.append('include_inactive', String(params.include_inactive))
  if (params?.limit) searchParams.append('limit', String(params.limit))
  const query = searchParams.toString()
  return fetchAPI(`/admin/ops/alerts${query ? `?${query}` : ''}`)
}

export async function getOpsContainerHistory(params?: {
  container_name?: string
  app_name?: string
  metric_name?: string
  kind?: string
  since_minutes?: number
  limit?: number
}): Promise<OpsMetricSnapshot[]> {
  const searchParams = new URLSearchParams()
  if (params?.container_name) searchParams.append('container_name', params.container_name)
  if (params?.app_name) searchParams.append('app_name', params.app_name)
  if (params?.metric_name) searchParams.append('metric_name', params.metric_name)
  if (params?.kind) searchParams.append('kind', params.kind)
  if (params?.since_minutes) searchParams.append('since_minutes', String(params.since_minutes))
  if (params?.limit) searchParams.append('limit', String(params.limit))
  const query = searchParams.toString()
  return fetchAPI(`/admin/ops/container-history${query ? `?${query}` : ''}`)
}

// Provision workspace for a user (admin)
export interface ProvisionResult {
  status: 'provisioned' | 'exists'
  workspace: {
    container_id: string
    container_name: string
    email: string
    created?: string
  }
  container_status?: string
}

export async function provisionWorkspace(userId: number): Promise<ProvisionResult> {
  return fetchAPI(`/admin/users/${userId}/provision`, { method: 'POST' })
}

// Impersonation
export interface ImpersonationStatus {
  impersonating: boolean
  original_admin?: string
  target_user?: string
}

export async function impersonateUser(userId: number): Promise<void> {
  // This endpoint returns a new session cookie, so we don't use fetchAPI
  const response = await fetch(
    withMountBase(`/auth/impersonate/${userId}`),
    withJsonHeaders({ method: 'POST' })
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to impersonate')
  }

  // Reload page to apply new session
  window.location.href = withMountBase('/')
}

export async function stopImpersonating(): Promise<void> {
  const response = await fetch(
    withMountBase('/auth/stop-impersonating'),
    withJsonHeaders({ method: 'POST' })
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to stop impersonating')
  }

  // Reload page to apply restored session
  window.location.href = withMountBase('/')
}

export async function getImpersonationStatus(): Promise<ImpersonationStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/impersonation-status'),
    { impersonating: false }
  )
}

// User Detail (admin)
export interface UserWorkspace {
  id: number
  container_id: string
  container_name: string
  status: string
  live_status?: string
  live_status_error?: string
  created_at: string | null
  updated_at: string | null
  last_accessed: string | null
  memory_usage?: number
  memory_limit?: number
  memory_percent?: number
  cpu_percent?: number
  container_created?: string
  container_started?: string
  // Health monitoring fields
  health_status?: string
  last_health_check?: string
  health_check_failures?: number
  health_message?: string
}

export interface UserDetail {
  id: number
  email: string
  name: string | null
  is_admin: boolean
  is_active: boolean
  created_at: string | null
  last_login: string | null
  settings: Record<string, unknown>
  workspace: UserWorkspace | null
}

export async function getUserDetail(userId: number): Promise<UserDetail> {
  return fetchAPI(`/admin/users/${userId}`)
}

export interface WorkspaceLogs {
  container_name: string
  tail: number
  logs: string
}

export async function getUserWorkspaceLogs(userId: number, tail: number = 100): Promise<WorkspaceLogs> {
  return fetchAPI(`/admin/users/${userId}/logs?tail=${tail}`)
}

export async function stopUserWorkspace(userId: number): Promise<{ status: string; container: string; live_status: string }> {
  return fetchAPI(`/admin/users/${userId}/stop-workspace`, { method: 'POST' })
}

export async function startUserWorkspace(userId: number): Promise<{ status: string; container: string; live_status: string }> {
  return fetchAPI(`/admin/users/${userId}/start-workspace`, { method: 'POST' })
}

export async function restartUserWorkspace(userId: number): Promise<{ status: string; container: string; live_status: string }> {
  return fetchAPI(`/admin/users/${userId}/restart-workspace`, { method: 'POST' })
}

export async function deleteUserWorkspace(userId: number, deleteData: boolean = false): Promise<{ status: string; container: string; volume_removed: boolean }> {
  return fetchAPI(`/admin/users/${userId}/workspace?delete_data=${deleteData}`, { method: 'DELETE' })
}

// Health monitoring
export interface HealthHistoryEvent {
  id: number
  workspace_id: number
  container_name: string
  status: string
  message: string | null
  details: Record<string, unknown>
  created_at: string
}

export async function getHealthHistory(containerName?: string, limit: number = 50): Promise<HealthHistoryEvent[]> {
  const params = new URLSearchParams()
  if (containerName) params.append('container_name', containerName)
  params.append('limit', limit.toString())
  return fetchAPI(`/admin/health/history?${params}`)
}

export async function triggerHealthCheck(containerName: string): Promise<{ container_name: string; health_status: string; message: string }> {
  return fetchAPI(`/admin/workspaces/${containerName}/health-check`, { method: 'POST' })
}

// Activity log — unified feed: audit_log entries (privileged actions) plus
// platform_events entries (page visits). source discriminates which table
// the row came from; slot is set on platform_event rows for app_visit
// events ("prod" / "dev" / "branch").
export type ActivitySource = 'audit_log' | 'platform_event'
export type AppSlot = 'prod' | 'dev' | 'branch'

export interface AuditLogEvent {
  id: string
  source: ActivitySource
  user_id: number | null
  action: string
  resource_type: string | null
  resource_id: string | null
  details: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  slot: AppSlot | null
  email: string | null
  user_name: string | null
}

export interface ActivityLogResponse {
  events: AuditLogEvent[]
  total: number
  limit: number
  offset: number
}

interface ActivityFilters {
  user_id?: number
  action?: string
  resource_type?: string
  resource_id?: string
  slot?: AppSlot
  source?: ActivitySource
  limit?: number
  offset?: number
}

function activitySearchParams(params?: ActivityFilters): URLSearchParams {
  const sp = new URLSearchParams()
  if (params?.user_id) sp.append('user_id', params.user_id.toString())
  if (params?.action) sp.append('action', params.action)
  if (params?.resource_type) sp.append('resource_type', params.resource_type)
  if (params?.resource_id) sp.append('resource_id', params.resource_id)
  if (params?.slot) sp.append('slot', params.slot)
  if (params?.source) sp.append('source', params.source)
  if (params?.limit) sp.append('limit', params.limit.toString())
  if (params?.offset) sp.append('offset', params.offset.toString())
  return sp
}

export async function getActivityLog(params?: ActivityFilters): Promise<ActivityLogResponse> {
  return fetchAPI(`/admin/activity?${activitySearchParams(params)}`)
}

export async function getUserActivityLog(
  userId: number,
  params?: Omit<ActivityFilters, 'user_id'>,
): Promise<ActivityLogResponse & { user_id: number; email: string }> {
  return fetchAPI(`/admin/users/${userId}/activity?${activitySearchParams(params)}`)
}

export async function getAppActivityLog(
  appName: string,
  params?: Omit<ActivityFilters, 'resource_id'>,
): Promise<ActivityLogResponse & { app_name: string }> {
  return fetchAPI(`/admin/apps/${encodeURIComponent(appName)}/activity?${activitySearchParams(params)}`)
}

// File browser
export interface FileEntry {
  name: string
  path: string
  is_dir: boolean
  is_file: boolean
  size: number | null
  modified: number
  is_blocked: boolean
}

export interface FilesListResponse {
  path: string
  base_dir: string
  entries: FileEntry[]
}

export interface FileContentResponse {
  path: string
  filename: string
  size: number
  content: string
}

export async function listUserFiles(userId: number, path: string = ''): Promise<FilesListResponse> {
  const params = path ? `?path=${encodeURIComponent(path)}` : ''
  return fetchAPI(`/admin/users/${userId}/files${params}`)
}

export async function getUserFileContent(userId: number, path: string): Promise<FileContentResponse> {
  return fetchAPI(`/admin/users/${userId}/files/content?path=${encodeURIComponent(path)}`)
}

// ============================================================================
// Platform Settings (Admin)
// ============================================================================

export interface PlatformSettings {
  id: number
  category: string
  settings: Record<string, unknown>
  updated_at: string | null
  updated_by: number | null
}

export async function listPlatformSettings(): Promise<PlatformSettings[]> {
  return fetchAPI('/admin/platform-settings')
}

export async function getPlatformSettings(category: string): Promise<PlatformSettings> {
  return fetchAPI(`/admin/platform-settings/${category}`)
}

export async function updatePlatformSettings(category: string, settings: Record<string, unknown>): Promise<PlatformSettings> {
  return fetchAPI(`/admin/platform-settings/${category}`, {
    method: 'PATCH',
    body: JSON.stringify({ settings }),
  })
}

export async function resetPlatformSettings(category: string): Promise<PlatformSettings> {
  return fetchAPI(`/admin/platform-settings/${category}/reset`, { method: 'POST' })
}

// ============================================================================
// Claude Code Configuration (Admin - Read-Only)
// ============================================================================

export interface ClaudeConfigOverview {
  agents: { name: string; description: string; filename: string }[]
  skills: { name: string; description: string; directory: string }[]
  hooks: { event: string; command: string; type: string }[]
  mcp_servers: { name: string; command: string; args: string[] }[]
  settings: Record<string, unknown>
  template: { filename: string; lines: number }
  rules: { name: string; description: string; filename: string }[]
  gsd_agents: { name: string; description: string; filename: string }[]
  gsd_commands: { name: string; filename: string }[]
  marketplace: {
    available: boolean
    name: string
    plugins: { name: string; description: string }[]
  }
}

export async function getClaudeConfig(): Promise<ClaudeConfigOverview> {
  return fetchAPI('/admin/claude-config')
}

export async function pushClaudeConfig(): Promise<{ status: string; message: string }> {
  return fetchAPI('/admin/claude-config/push', { method: 'POST' })
}

// ============================================================================
// Enhanced User Settings
// ============================================================================

export interface MergedSettings {
  terminal: Record<string, unknown>
  vscode: Record<string, unknown>
  browser: Record<string, unknown>
  dashboard: Record<string, unknown>
  claude_code: Record<string, unknown>
  // Legacy fields
  theme: string
  editor_font_size: number
  terminal_font_size: number
  vnc_resolution: string
  auto_save: boolean
  // Customization tracking
  customized_categories: string[]
}

export async function getMergedSettings(): Promise<MergedSettings> {
  return fetchAPI('/settings/merged')
}

export async function resetSettingsCategory(category: string): Promise<{ status: string; message: string }> {
  return fetchAPI(`/settings/reset/${category}`, { method: 'POST' })
}

// ============================================================================
// Service Tokens (Personal API Tokens for JIRA/Confluence)
// ============================================================================

export interface ServiceToken {
  service: string
  configured: boolean
  updated_at: string | null
}

export interface ServiceTokenListResponse {
  tokens: ServiceToken[]
}

export async function listServiceTokens(): Promise<ServiceTokenListResponse> {
  return fetchAPI('/settings/tokens')
}

export async function setServiceToken(service: string, token: string): Promise<{ status: string; service: string }> {
  return fetchAPI(`/settings/tokens/${service}`, {
    method: 'PUT',
    body: JSON.stringify({ token }),
  })
}

export async function deleteServiceToken(service: string): Promise<{ status: string; service: string; deleted: boolean }> {
  return fetchAPI(`/settings/tokens/${service}`, { method: 'DELETE' })
}

// ============================================================================
// Personal Access Tokens (PAT) for CC API
// ============================================================================

export interface PersonalAccessToken {
  id: number
  name: string
  token_prefix: string
  scopes: string[]
  expires_at: string | null
  last_used_at: string | null
  is_active: boolean
  created_at: string
}

export interface CreatePersonalAccessTokenRequest {
  name: string
  scopes?: string[]
  expires_in_days?: number | null
}

export interface CreatedPersonalAccessToken extends PersonalAccessToken {
  token: string
}

export async function listPersonalAccessTokens(): Promise<PersonalAccessToken[]> {
  return fetchAPI('/pat/')
}

export async function createPersonalAccessToken(
  data: CreatePersonalAccessTokenRequest
): Promise<CreatedPersonalAccessToken> {
  return fetchAPI('/pat/', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function revokePersonalAccessToken(tokenId: number): Promise<void> {
  return fetchAPI(`/pat/${tokenId}`, { method: 'DELETE' })
}

// ============================================================================
// Workspace Resource Management
// ============================================================================

export interface WorkspaceResourceOverrides {
  cpu_cores?: number
  cpu_limit?: string
  memory_mb?: number
  memory_limit?: string
  disk_gb?: number
  gpu_enabled?: boolean
  gpu_count?: number
  network_bandwidth?: string
  idle_timeout_minutes?: number
  max_runtime_hours?: number
}

export interface MergedWorkspaceResources {
  cpu_cores: number
  cpu_limit: string
  memory_mb: number
  memory_limit: string
  disk_gb: number
  gpu_enabled: boolean
  gpu_count: number
  network_bandwidth: string
  idle_timeout_minutes: number
  max_runtime_hours: number
  is_customized: boolean
}

export interface WorkspaceInfo {
  id: number
  user_id: number
  container_id: string | null
  container_name: string | null
  status: string
  health_status: string
  created_at: string | null
  updated_at: string | null
  last_accessed: string | null
  resource_overrides: WorkspaceResourceOverrides
}

export interface AdminWorkspaceInfo extends WorkspaceInfo {
  user_email: string
  user_name: string | null
}

// User workspace resource management
export async function getMyWorkspace(): Promise<WorkspaceInfo> {
  return fetchAPI('/workspaces/my')
}

export async function getMyWorkspaceResources(): Promise<MergedWorkspaceResources> {
  return fetchAPI('/workspaces/my/resources')
}

export async function updateMyWorkspaceResources(overrides: WorkspaceResourceOverrides): Promise<MergedWorkspaceResources> {
  return fetchAPI('/workspaces/my/resources', {
    method: 'PATCH',
    body: JSON.stringify(overrides),
  })
}

export async function resetMyWorkspaceResources(): Promise<{ status: string; message: string }> {
  return fetchAPI('/workspaces/my/resources', { method: 'DELETE' })
}

// Admin workspace resource management
export async function listAllWorkspaces(status?: string): Promise<AdminWorkspaceInfo[]> {
  const params = status ? `?status=${encodeURIComponent(status)}` : ''
  return fetchAPI(`/workspaces${params}`)
}

export async function getWorkspaceById(workspaceId: number): Promise<AdminWorkspaceInfo> {
  return fetchAPI(`/workspaces/${workspaceId}`)
}

export async function updateWorkspaceResources(workspaceId: number, overrides: WorkspaceResourceOverrides): Promise<AdminWorkspaceInfo> {
  return fetchAPI(`/workspaces/${workspaceId}/resources`, {
    method: 'PATCH',
    body: JSON.stringify(overrides),
  })
}

export async function resetWorkspaceResources(workspaceId: number): Promise<{ status: string; workspace_id: number }> {
  return fetchAPI(`/workspaces/${workspaceId}/resources`, { method: 'DELETE' })
}

// ============================================================================
// App Resource Management
// ============================================================================

export interface AppResourceOverrides {
  cpu_request?: string
  cpu_limit?: string
  memory_request?: string
  memory_limit?: string
  replicas_min?: number
  replicas_max?: number
  disk_gb?: number
  port_range_start?: number
  health_check_enabled?: boolean
  auto_restart?: boolean
  log_retention_days?: number
}

export interface MergedAppResources {
  cpu_request: string
  cpu_limit: string
  memory_request: string
  memory_limit: string
  replicas_min: number
  replicas_max: number
  disk_gb: number
  port_range_start: number
  health_check_enabled: boolean
  auto_restart: boolean
  log_retention_days: number
  is_customized: boolean
}

export async function getAppResources(appName: string): Promise<MergedAppResources> {
  return fetchAPI(`/apps/${appName}/resources`)
}

export async function updateAppResources(appName: string, overrides: AppResourceOverrides): Promise<MergedAppResources> {
  return fetchAPI(`/apps/${appName}/resources`, {
    method: 'PATCH',
    body: JSON.stringify(overrides),
  })
}

export async function resetAppResources(appName: string): Promise<{ status: string; app_name: string }> {
  return fetchAPI(`/apps/${appName}/resources`, { method: 'DELETE' })
}

// =============================================================================
// App Sessions (Git Worktree-based Sessions)
// =============================================================================

export interface SourcePreflightWarning {
  code: string
  severity: string
  message: string
}

export interface SourceSummary {
  available?: boolean
  savedVersionId?: string | null
  changedFileCount?: number
  hasUnsavedChanges?: boolean
  savedChangeCount?: number
  broughtBack?: boolean
  canBringBack?: boolean
  sourceLabel?: string | null
  sourceSessionId?: string | null
  sourceSessionName?: string | null
  includedUnsavedChanges?: boolean
  reviewRequest?: Record<string, unknown> | null
  advanced: Record<string, unknown>
}

export interface SourcePreflightResponse {
  action: string
  actionLabel?: string
  safe: boolean
  summary: string
  warnings: SourcePreflightWarning[]
  sourceSummary?: SourceSummary
  liveApp?: LiveAppSource
  advanced: Record<string, unknown>
}

export interface LiveAppSource {
  status: string | null
  sourceLabel?: string | null
  sourceSessionId?: string | null
  sourceSessionName?: string | null
  savedVersionId?: string | null
  includedUnsavedChanges?: boolean
  madeLiveByUserId?: number | null
  madeLiveAt?: string | null
  buildId?: string | null
  buildLogAvailable?: boolean
  advanced: Record<string, unknown>
}

export interface LiveAppSourceResponse {
  liveApp: LiveAppSource
}

export interface AppSession {
  id: string
  app_id: number
  name: string
  branch: string
  worktree_path: string | null
  tmux_session: string
  status: 'active' | 'paused' | 'archived'
  created_by: number | null
  created_by_email?: string
  created_at: string | null
  last_accessed_at: string | null
  last_accessed_by: number | null
  locked_by: number | null
  locked_by_email?: string | null
  locked_at: string | null
  presence: SessionPresence[]
  branch_preview_url?: string  // URL for branch preview (non-main sessions)
  source_summary?: SourceSummary
}

export interface SessionPresence {
  user_id: number
  email: string
  connected_at: string | null
}

export interface CreateSessionRequest {
  name: string
}

export interface MergeSessionResult {
  success: boolean
  message: string
  conflicts?: string
}

export async function listAppSessions(appName: string, includeArchived?: boolean): Promise<{ sessions: AppSession[] }> {
  const params = includeArchived ? '?include_archived=true' : ''
  // Note: Using the router service endpoint directly
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions${params}`,
    undefined,
    'Failed to list sessions'
  )
}

export async function createAppSession(appName: string, name: string): Promise<{ session: AppSession }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    },
    'Failed to create session'
  )
}

export async function getAppSession(appName: string, sessionId: string): Promise<{ session: AppSession }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}`,
    undefined,
    'Failed to get session'
  )
}

export async function getAppSessionSourceStatus(
  appName: string,
  sessionId: string,
): Promise<{ source_summary: SourceSummary }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/sessions/${encodeURIComponent(sessionId)}/source-status`,
    undefined,
    'Failed to get work session source status'
  )
}

export async function saveWorkspaceChanges(
  appName: string,
  payload: { session_name?: string; message?: string },
): Promise<{
  saved: boolean
  message: string
  changed_file_count: number
  commit_sha: string | null
  short_commit_sha: string | null
  session_name: string
}> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/save`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to save changes'
  )
}

export async function preflightBringChangesBack(
  appName: string,
  sessionId: string,
): Promise<SourcePreflightResponse> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/sessions/${encodeURIComponent(sessionId)}/preflight/bring-back`,
    { method: 'POST' },
    'Failed to check whether changes can be applied'
  )
}

export async function preflightCloseWorkSession(
  appName: string,
  sessionId: string,
): Promise<SourcePreflightResponse> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/sessions/${encodeURIComponent(sessionId)}/preflight/close`,
    { method: 'POST' },
    'Failed to check close work session safety'
  )
}

export async function preflightDeleteWorkSession(
  appName: string,
  sessionId: string,
): Promise<SourcePreflightResponse> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/sessions/${encodeURIComponent(sessionId)}/preflight/delete`,
    { method: 'POST' },
    'Failed to check delete work session safety'
  )
}

export async function getLiveAppSource(appName: string): Promise<LiveAppSourceResponse> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/live-app/source`,
    undefined,
    'Failed to get live app source'
  )
}

export async function preflightMakeAppLive(
  appName: string,
  sourceSessionId?: string | null,
): Promise<SourcePreflightResponse> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/live-app/preflight/make-live`,
    {
      method: 'POST',
      body: JSON.stringify(sourceSessionId ? { source_session_id: sourceSessionId } : {}),
    },
    'Failed to check whether the app can be published'
  )
}

export async function openAppSession(appName: string, sessionId: string): Promise<{
  session: AppSession
  terminal_url: string
  chrome_url: string
}> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}/open`,
    { method: 'POST' },
    'Failed to open session'
  )
}

export async function lockAppSession(appName: string, sessionId: string): Promise<{ session: AppSession }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}/lock`,
    { method: 'POST' },
    'Failed to lock session'
  )
}

export async function unlockAppSession(appName: string, sessionId: string): Promise<{ session: AppSession }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}/unlock`,
    { method: 'POST' },
    'Failed to unlock session'
  )
}

export async function mergeAppSession(
  appName: string,
  sessionId: string,
  deleteAfterMerge?: boolean
): Promise<MergeSessionResult> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}/merge`,
    {
      method: 'POST',
      body: JSON.stringify({ delete_after_merge: deleteAfterMerge ?? false }),
    },
    'Failed to merge session'
  )
}

export async function archiveAppSession(appName: string, sessionId: string): Promise<{ session: AppSession }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}/archive`,
    { method: 'POST' },
    'Failed to archive session'
  )
}

// Session Activity (terminal tab activity badges)
export interface SessionActivity {
  session: string
  state: 'working' | 'waiting' | 'idle'
  idle_seconds: number
}

export async function getSessionActivity(
  appName: string,
  sessions: string[]
): Promise<SessionActivity[]> {
  const sessionsParam = sessions.join(',')
  const response = await fetch(
    `${API_BASE}/deployed-apps/${encodeURIComponent(appName)}/session-activity?sessions=${encodeURIComponent(sessionsParam)}`,
    withJsonHeaders({ credentials: 'include' })
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to get session activity')
  }
  const data = await response.json()
  return data.activities || []
}

export async function deleteAppSession(appName: string, sessionId: string): Promise<{ status: string }> {
  return fetchJsonDirect(
    `${API_BASE}/deployed-apps/${appName}/sessions/${sessionId}`,
    { method: 'DELETE' },
    'Failed to delete session'
  )
}

// =============================================================================
// AuthDB Account Linking
// =============================================================================

export interface AuthDBLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
}

export async function getAuthDBLinkStatus(): Promise<AuthDBLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/authdb/status'),
    { linked: false, service: 'authdb' }
  )
}

export async function unlinkAuthDB(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/authdb/unlink'),
    { method: 'POST' },
    'Failed to unlink AuthDB account'
  )
}

export function getAuthDBLinkURL(): string {
  return '/auth/authdb/link'
}

// =============================================================================
// GitHub Account Linking
// =============================================================================

export interface GitHubLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  github_username?: string
  scope?: string
}

export async function getGitHubLinkStatus(): Promise<GitHubLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/github/status'),
    { linked: false, service: 'github' }
  )
}

export async function unlinkGitHub(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/github/unlink'),
    { method: 'POST' },
    'Failed to unlink GitHub account'
  )
}

export function getGitHubLinkURL(): string {
  return '/auth/github/link'
}

// =============================================================================
// GitLab Account Linking
// =============================================================================

export interface GitLabLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  gitlab_username?: string
  gitlab_name?: string
  gitlab_email?: string
  gitlab_host?: string
  instance_url?: string
  scope?: string
}

export async function getGitLabLinkStatus(): Promise<GitLabLinkStatus> {
  const response = await fetch('/auth/gitlab/status', {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!response.ok) {
    return { linked: false, service: 'gitlab' }
  }
  return response.json()
}

export async function unlinkGitLab(): Promise<{ unlinked: boolean; service: string }> {
  const response = await fetch('/auth/gitlab/unlink', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to unlink' }))
    throw new Error(error.detail || 'Failed to unlink GitLab account')
  }
  return response.json()
}

export function getGitLabLinkURL(): string {
  return '/auth/gitlab/link'
}

// =============================================================================
// Google Drive Account Linking
// =============================================================================

export interface GoogleDriveLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  email?: string
  scopes_changed?: boolean
}

export interface GoogleWorkspaceScope {
  key: string
  label: string
  description: string
  required: boolean
  enabled: boolean
}

export async function getGoogleDriveLinkStatus(): Promise<GoogleDriveLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/google-drive/status'),
    { linked: false, service: 'google-drive' }
  )
}

export async function unlinkGoogleDrive(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/google-drive/unlink'),
    { method: 'POST' },
    'Failed to unlink Google Drive'
  )
}

export function getGoogleDriveLinkURL(): string {
  return '/auth/google-drive/link'
}

export async function getGoogleDriveScopes(): Promise<GoogleWorkspaceScope[]> {
  const response = await fetch(withMountBase('/auth/google-drive/scopes'), withJsonHeaders())
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch Google Workspace scopes')
  }
  const data = await response.json()
  return data.scopes
}

export async function updateGoogleDriveScopes(scopes: Record<string, boolean>): Promise<Record<string, boolean>> {
  const response = await fetch(
    withMountBase('/auth/google-drive/scopes'),
    withJsonHeaders({
      method: 'PUT',
      body: JSON.stringify({ scopes }),
    })
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to update scopes')
  }
  const data = await response.json()
  return data.scopes
}

// =============================================================================
// Slack Account Linking
// =============================================================================

export interface SlackLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  team_name?: string
  email?: string
  scopes_changed?: boolean
}

export interface SlackScope {
  key: string
  label: string
  description: string
  required: boolean
  enabled: boolean
}

export async function getSlackLinkStatus(): Promise<SlackLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/slack/status'),
    { linked: false, service: 'slack' }
  )
}

export async function unlinkSlack(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/slack/unlink'),
    { method: 'POST' },
    'Failed to unlink Slack'
  )
}

export function getSlackLinkURL(): string {
  return '/auth/slack/link'
}

export async function getSlackScopes(): Promise<SlackScope[]> {
  const response = await fetch(withMountBase('/auth/slack/scopes'), withJsonHeaders())
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch Slack scopes')
  }
  const data = await response.json()
  return data.scopes
}

export async function updateSlackScopes(scopes: Record<string, boolean>): Promise<Record<string, boolean>> {
  const response = await fetch(
    withMountBase('/auth/slack/scopes'),
    withJsonHeaders({
      method: 'PUT',
      body: JSON.stringify({ scopes }),
    })
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to update Slack scopes')
  }
  const data = await response.json()
  return data.scopes
}

// =============================================================================
// MS Dynamics / Business Central Account Linking
// =============================================================================

export interface DynamicsLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  ms_email?: string
  ms_display_name?: string
  bc_environment?: string
}

export async function getDynamicsLinkStatus(): Promise<DynamicsLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/dynamics/status'),
    { linked: false, service: 'dynamics' }
  )
}

export async function unlinkDynamics(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/dynamics/unlink'),
    { method: 'POST' },
    'Failed to unlink Dynamics account'
  )
}

export function getDynamicsLinkURL(): string {
  return '/auth/dynamics/link'
}

// =============================================================================
// PowerApps / Dataverse Account Linking
// =============================================================================

export interface PowerAppsLinkStatus {
  linked: boolean
  service: string
  configured?: boolean
  ms_email?: string
  ms_display_name?: string
}

export async function getPowerAppsLinkStatus(): Promise<PowerAppsLinkStatus> {
  return fetchJsonDirectOrDefault(
    withMountBase('/auth/powerapps/status'),
    { linked: false, service: 'powerapps' }
  )
}

export async function unlinkPowerApps(): Promise<{ unlinked: boolean; service: string }> {
  return fetchJsonDirect(
    withMountBase('/auth/powerapps/unlink'),
    { method: 'POST' },
    'Failed to unlink PowerApps account'
  )
}

export function getPowerAppsLinkURL(): string {
  return '/auth/powerapps/link'
}

// =============================================================================
// Project Import (URL Clone)
// =============================================================================

export interface ImportStatus {
  id: number
  app_name: string
  source_type: string
  source_url: string
  status: 'pending' | 'cloning' | 'converting' | 'deploying' | 'uploading' | 'complete' | 'failed'
  error_message: string | null
  created_at: string
  updated_at: string
}

export interface ImportTimelineEvent {
  id: number
  attempt_id: number
  attempt_no: number
  stage: string
  event_type: string
  message: string | null
  details: Record<string, unknown>
  created_at: string
}

export interface ImportTimeline {
  import_id: number
  app_name: string
  status: string
  current_attempt_id: number | null
  current_attempt_no: number | null
  current_stage: string | null
  events: ImportTimelineEvent[]
}

export async function createImportFromURL(data: {
  url: string
  name?: string
  display_name?: string
}): Promise<{ import_id: number; app_name: string }> {
  return fetchAPI('/apps/import', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getImportStatus(importId: number): Promise<ImportStatus> {
  return fetchAPI(`/apps/imports/${importId}`)
}

export async function getImportTimeline(importId: number, limit: number = 50): Promise<ImportTimeline> {
  const clamped = Math.max(1, Math.min(Math.floor(limit), 200))
  return fetchAPI(`/apps/imports/${importId}/timeline?limit=${clamped}`)
}

// =============================================================================
// GitHub Repo Import
// =============================================================================

export interface GitHubRepo {
  full_name: string
  name: string
  owner: string
  private: boolean
  default_branch: string
  description: string | null
  updated_at: string
  language: string | null
}

export interface GitHubReposResponse {
  repos: GitHubRepo[]
  page: number
  has_more: boolean
}

export interface GitHubBranch {
  name: string
}

export interface GitHubBranchesResponse {
  branches: GitHubBranch[]
  has_more: boolean
}

export async function listGitHubRepos(
  page: number = 1,
  perPage: number = 30,
): Promise<GitHubReposResponse> {
  return fetchAPI(`/apps/github/repos?page=${page}&per_page=${perPage}`)
}

export async function listGitHubBranches(
  owner: string,
  repo: string,
): Promise<GitHubBranchesResponse> {
  return fetchAPI(`/apps/github/repos/${owner}/${repo}/branches?per_page=100`)
}

export async function createImportFromGitHub(data: {
  repo_full_name: string
  branch?: string
  name?: string
  display_name?: string
}): Promise<{ import_id: number; app_name: string }> {
  return fetchAPI('/apps/import-github', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// =============================================================================
// GitLab Repo Import
// =============================================================================

export interface GitLabRepo {
  id: number | string
  full_name: string
  path_with_namespace: string
  name: string
  default_branch: string
  description: string | null
  updated_at: string
  web_url?: string
  instance_url?: string
  visibility?: string | null
  private?: boolean
  language?: string | null
}

export interface GitLabReposResponse {
  repos: GitLabRepo[]
  page: number
  has_more: boolean
  instance_url?: string
}

export interface GitLabBranch {
  name: string
  default?: boolean
  committed_date?: string | null
}

export interface GitLabBranchesResponse {
  branches: GitLabBranch[]
  has_more: boolean
  page: number
  default_branch?: string | null
}

function normalizeGitLabRepo(repo: unknown): GitLabRepo | null {
  if (!repo || typeof repo !== 'object') return null

  const candidate = repo as Record<string, unknown>
  const fullName = String(candidate.full_name || candidate.path_with_namespace || '').trim()
  const name = String(candidate.name || '').trim()
  const updatedAt = String(candidate.updated_at || candidate.last_activity_at || '').trim()

  if (!fullName || !name || !updatedAt) {
    return null
  }

  const rawId = candidate.id
  const visibility = candidate.visibility != null ? String(candidate.visibility) : null
  const isPrivate = typeof candidate.private === 'boolean'
    ? candidate.private
    : visibility === 'private'

  return {
    id: typeof rawId === 'number' || typeof rawId === 'string' ? rawId : fullName,
    full_name: fullName,
    path_with_namespace: String(candidate.path_with_namespace || fullName).trim(),
    name,
    default_branch: String(candidate.default_branch || 'main').trim() || 'main',
    description: candidate.description == null ? null : String(candidate.description),
    updated_at: updatedAt,
    web_url: candidate.web_url ? String(candidate.web_url) : undefined,
    instance_url: candidate.instance_url ? String(candidate.instance_url) : undefined,
    visibility,
    private: isPrivate,
    language: candidate.language == null ? null : String(candidate.language),
  }
}

function normalizeGitLabReposResponse(payload: unknown, fallbackPage: number): GitLabReposResponse {
  if (Array.isArray(payload)) {
    return {
      repos: payload.map(normalizeGitLabRepo).filter((repo): repo is GitLabRepo => repo !== null),
      page: fallbackPage,
      has_more: false,
    }
  }

  if (!payload || typeof payload !== 'object') {
    return { repos: [], page: fallbackPage, has_more: false }
  }

  const result = payload as Record<string, unknown>
  const repoItems = Array.isArray(result.repos)
    ? result.repos
    : Array.isArray(result.projects)
      ? result.projects
      : []

  return {
    repos: repoItems.map(normalizeGitLabRepo).filter((repo): repo is GitLabRepo => repo !== null),
    page: typeof result.page === 'number' ? result.page : fallbackPage,
    has_more: Boolean(result.has_more),
    instance_url: result.instance_url ? String(result.instance_url) : undefined,
  }
}

function normalizeGitLabBranchesResponse(payload: unknown, fallbackPage: number): GitLabBranchesResponse {
  if (Array.isArray(payload)) {
    return {
      branches: payload
        .map((branch): GitLabBranch | null => {
          if (!branch || typeof branch !== 'object') return null
          const obj = branch as Record<string, unknown>
          const name = String(obj.name || '').trim()
          if (!name) return null
          const committedDate = typeof obj.committed_date === 'string' ? obj.committed_date : null
          return { name, default: Boolean(obj.default), committed_date: committedDate }
        })
        .filter((branch): branch is GitLabBranch => branch !== null),
      has_more: false,
      page: fallbackPage,
    }
  }

  if (!payload || typeof payload !== 'object') {
    return { branches: [], has_more: false, page: fallbackPage }
  }

  const result = payload as Record<string, unknown>
  const branchItems = Array.isArray(result.branches) ? result.branches : []
  const defaultBranchRaw = result.default_branch
  const defaultBranch = typeof defaultBranchRaw === 'string' && defaultBranchRaw.trim()
    ? defaultBranchRaw.trim()
    : null

  return {
    branches: branchItems
      .map((branch): GitLabBranch | null => {
        if (!branch || typeof branch !== 'object') return null
        const obj = branch as Record<string, unknown>
        const name = String(obj.name || '').trim()
        if (!name) return null
        const committedDate = typeof obj.committed_date === 'string' ? obj.committed_date : null
        return {
          name,
          default: Boolean(obj.default) || (defaultBranch != null && name === defaultBranch),
          committed_date: committedDate,
        }
      })
      .filter((branch): branch is GitLabBranch => branch !== null),
    has_more: Boolean(result.has_more),
    page: typeof result.page === 'number' ? result.page : fallbackPage,
    default_branch: defaultBranch,
  }
}

async function fetchGitLabReposPayload(page: number, perPage: number): Promise<unknown> {
  return fetchAPI(`/apps/gitlab/projects?page=${page}&per_page=${perPage}`)
}

export async function listGitLabRepos(
  page: number = 1,
  perPage: number = 30,
): Promise<GitLabReposResponse> {
  const payload = await fetchGitLabReposPayload(page, perPage)
  return normalizeGitLabReposResponse(payload, page)
}

async function fetchGitLabBranchesPayload(
  repo: GitLabRepo,
  perPage: number,
  page: number,
  search?: string,
): Promise<unknown> {
  const params = new URLSearchParams({
    project_path: repo.path_with_namespace,
    per_page: String(perPage),
  })
  if (page > 1) {
    params.set('page', String(page))
  }
  if (search && search.trim()) {
    params.set('search', search.trim())
  }
  return fetchAPI(`/apps/gitlab/projects/branches?${params.toString()}`)
}

export async function listGitLabBranches(
  repo: GitLabRepo,
  options: { search?: string; page?: number } = {},
): Promise<GitLabBranchesResponse> {
  const page = options.page && options.page > 0 ? options.page : 1
  const payload = await fetchGitLabBranchesPayload(repo, 100, page, options.search)
  return normalizeGitLabBranchesResponse(payload, page)
}

export async function createImportFromGitLab(data: {
  project_id?: number | string
  repo_full_name?: string
  path_with_namespace?: string
  branch?: string
  name?: string
  display_name?: string
  instance_url?: string
  web_url?: string
}): Promise<{ import_id: number; app_name: string }> {
  return fetchAPI('/apps/import-gitlab', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// =============================================================================
// Repository Binding & Git History
// =============================================================================

export interface RepoBinding {
  id: number
  appId: number
  provider: 'github' | 'gitlab' | string
  repoFullName: string
  repoWebUrl: string | null
  defaultBranch: string
  trackedBranch: string
  syncMode: 'linked' | 'manual' | string
  webhookEnabled: boolean
  webhookId: string | null
  lastSeenSha: string | null
  lastDeployedSha: string | null
  lastSyncedVersionId?: string | null
  divergenceStatus: string
  divergenceAheadBy: number | null
  divergenceBehindBy: number | null
  lastComparedSha: string | null
  lastSyncStatus: string
  lastSyncMessage: string | null
  lastSyncTrigger: string | null
  lastSyncTriggeredAt: string | null
  lastSyncAt: string | null
  createdAt: string
  updatedAt: string
}

export interface RepoPreflightWarning {
  code: string
  severity: string
  message: string
}

export interface RepoPreflightResponse {
  action: 'sync_latest_changes' | 'share_for_review' | 'update_review_request' | string
  safe: boolean
  summary: string
  warnings: RepoPreflightWarning[]
  reviewRequest: Record<string, unknown> | null
  binding: RepoBinding | null
  advanced: Record<string, unknown>
}

export interface RepoSyncJob {
  id: number
  repoBindingId: number
  appId: number
  trigger: string
  status: string
  targetSha: string | null
  requestedByUserId: number | null
  errorMessage: string | null
  details: Record<string, unknown>
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface RepoBindingUpsertPayload {
  provider: 'github' | 'gitlab'
  repo_full_name: string
  default_branch: string
  tracked_branch: string
  sync_mode?: 'linked' | 'manual'
  webhook_enabled?: boolean
}

export interface CreateRemoteRepoPayload {
  provider: 'github' | 'gitlab'
  name: string
  owner?: string
  description?: string
  private?: boolean
  default_branch?: string
}

export interface CreatedRemoteRepository {
  provider: 'github' | 'gitlab' | string
  repoFullName: string
  name: string
  defaultBranch: string
  private: boolean
  webUrl: string | null
  cloneUrl: string | null
  raw: Record<string, unknown>
}

export interface InitialRepoPush {
  provider: string
  repoFullName: string
  remoteUrl: string
  headSha: string
  defaultBranch: string
  committedChanges: boolean
  currentBranch: string | null
  pushedBranches: string[]
  pushedTags: string[]
  skippedTags: string[]
}

export interface CreateRemoteRepoResponse {
  binding: RepoBinding
  repository: CreatedRemoteRepository
  initialPush: InitialRepoPush
}

export interface GitHistoryRemote {
  name: string
  fetchUrl: string | null
  pushUrl: string | null
}

export interface GitHistoryRef {
  name: string
  fullName: string
  kind: 'local' | 'remote' | 'tag' | 'other' | string
  targetSha: string
}

export interface GitHistoryWorktree {
  path: string
  displayPath: string
  branch: string | null
  headSha: string | null
  detached: boolean
  bare: boolean
  current: boolean
}

export interface GitHistoryCommit {
  graph: string
  sha: string
  shortSha: string
  parents: string[]
  authorName: string
  authorEmail: string | null
  authoredAt: string | null
  subject: string
  decorations: string[]
  body: string | null
}

export interface GitHistoryProviderBranch {
  name: string
  commitSha: string | null
  protected: boolean
  default: boolean
  merged: boolean | null
}

export interface GitHistoryResponse {
  isGitRepo: boolean
  repoPath: string
  currentBranch: string | null
  headSha: string | null
  statusSummary: string | null
  statusLines: string[]
  remotes: GitHistoryRemote[]
  refs: GitHistoryRef[]
  worktrees: GitHistoryWorktree[]
  providerBranches: GitHistoryProviderBranch[]
  commits: GitHistoryCommit[]
  truncated: boolean
  error: string | null
}

export interface PublishPullRequestResponse {
  binding: RepoBinding
  publication: {
    repoFullName: string
    baseBranch: string
    headBranch: string
    headSha: string
    prNumber: number | null
    prUrl: string | null
    prApiUrl: string | null
    draft: boolean
    reusedExistingPr: boolean
    hadWorkspaceChanges: boolean
  }
}

export interface PublishMergeRequestResponse {
  binding: RepoBinding
  publication: {
    repoFullName: string
    baseBranch: string
    headBranch: string
    headSha: string
    mrIid: number | null
    mrUrl: string | null
    mrApiUrl: string | null
    draft: boolean
    reusedExistingMr: boolean
    hadWorkspaceChanges: boolean
  }
}

export async function getRepoBinding(appName: string): Promise<RepoBinding | null> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding`)
}

export async function upsertRepoBinding(
  appName: string,
  payload: RepoBindingUpsertPayload,
): Promise<RepoBinding> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding`, {
    method: 'PUT',
    body: JSON.stringify({
      sync_mode: 'manual',
      webhook_enabled: false,
      ...payload,
    }),
  })
}

export async function createRemoteRepoBinding(
  appName: string,
  payload: CreateRemoteRepoPayload,
): Promise<CreateRemoteRepoResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/create-remote`, {
    method: 'POST',
    body: JSON.stringify({
      private: true,
      default_branch: 'main',
      ...payload,
    }),
  })
}

export async function deleteRepoBinding(appName: string): Promise<void> {
  await fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding`, {
    method: 'DELETE',
  })
}

export async function triggerRepoSync(
  appName: string,
  payload: { trigger?: 'manual' | 'webhook' | 'scheduled'; target_sha?: string | null } = {},
): Promise<RepoSyncJob> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/sync`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function listRepoSyncJobs(appName: string, limit: number = 20): Promise<RepoSyncJob[]> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/sync-jobs?limit=${limit}`)
}

export async function preflightSyncLatestChanges(appName: string): Promise<RepoPreflightResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/preflight/sync-latest`, {
    method: 'POST',
  })
}

export async function preflightShareForReview(appName: string): Promise<RepoPreflightResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/preflight/share-for-review`, {
    method: 'POST',
  })
}

export async function publishRepoPullRequest(
  appName: string,
  payload: {
    title?: string
    body?: string
    base_branch?: string
    head_branch?: string
    commit_message?: string
    draft?: boolean
    allow_empty_commit?: boolean
  } = {},
): Promise<PublishPullRequestResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/publish-pr`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function publishRepoMergeRequest(
  appName: string,
  payload: {
    title?: string
    body?: string
    base_branch?: string
    head_branch?: string
    commit_message?: string
    draft?: boolean
    allow_empty_commit?: boolean
  } = {},
): Promise<PublishMergeRequestResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/publish-mr`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function openRepoBranchPullRequest(
  appName: string,
  payload: {
    title?: string
    body?: string
    base_branch?: string
    head_branch: string
    draft?: boolean
  },
): Promise<PublishPullRequestResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/open-pr`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function openRepoBranchMergeRequest(
  appName: string,
  payload: {
    title?: string
    body?: string
    base_branch?: string
    head_branch: string
    draft?: boolean
  },
): Promise<PublishMergeRequestResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/repo-binding/open-mr`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getGitHistory(appName: string, limit: number = 120): Promise<GitHistoryResponse> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/git-history?limit=${limit}`)
}

// =============================================================================
// Folder Upload Import
// =============================================================================

export async function createImportFromUpload(
  archive: Blob,
  folderName: string,
  name?: string,
  displayName?: string,
  onProgress?: (percent: number) => void,
): Promise<{ import_id: number; app_name: string }> {
  const formData = new FormData()
  formData.append('archive', archive, 'upload.tar')
  formData.append('folder_name', folderName)
  if (name) formData.append('name', name)
  if (displayName) formData.append('display_name', displayName)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/apps/import-upload`)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText))
      } else {
        try {
          const err = JSON.parse(xhr.responseText)
          reject(new Error(err.detail || err.error?.message || 'Upload failed'))
        } catch {
          reject(new Error(`Upload failed (${xhr.status})`))
        }
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(formData)
  })
}

// =============================================================================
// Content API (Release Notes & User Guide)
// =============================================================================

export interface ReleaseChange {
  title: string
  description?: string | null
  category: string
  highlight: boolean
  image?: string | null
}

export interface ReleaseSection {
  heading: string
  content: string
}

export interface ReleaseNote {
  version: string
  title: string
  date: string
  summary: string
  hero_image?: string | null
  changes: ReleaseChange[]
  highlights: ReleaseChange[]
  sections: ReleaseSection[]
  guide_sections?: string[]
}

export interface ReleaseSummaryChange {
  title: string
  category: string
}

export interface ReleaseSummary {
  version: string
  title: string
  date: string
  summary: string
  hero_image?: string | null
  change_categories?: string[]
  changes?: ReleaseSummaryChange[]
}

// Enhanced Guide types
export interface GuideSectionMeta {
  slug: string
  title: string
  icon?: string
  order: number
  children: GuideSectionMeta[]
}

export interface GuideIndex {
  sections: GuideSectionMeta[]
  cross_references: Record<string, string[]>
}

export interface GuideContentEnhanced {
  slug: string
  title: string
  content: string
  parent: string | null
  breadcrumbs: { slug: string; title: string }[]
  related_releases: string[]
}

export interface GuideSearchResult {
  slug: string
  title: string
  excerpt: string
}

export interface ContentFeedback {
  user_vote: boolean | null
  up_count: number
  down_count: number
}

export async function listReleases(): Promise<ReleaseSummary[]> {
  return fetchAPI('/content/releases')
}

export async function getRelease(version: string): Promise<ReleaseNote> {
  return fetchAPI(`/content/releases/${encodeURIComponent(version)}`)
}

export async function getGuideIndex(): Promise<GuideIndex> {
  return fetchAPI('/content/guide')
}

export async function getGuideContent(section: string): Promise<GuideContentEnhanced> {
  return fetchAPI(`/content/guide/${encodeURIComponent(section)}`)
}

export async function searchGuide(query: string): Promise<GuideSearchResult[]> {
  return fetchAPI(`/content/guide/search?q=${encodeURIComponent(query)}`)
}

export async function getContentFeedback(type: string, slug: string): Promise<ContentFeedback> {
  return fetchAPI(`/content/feedback/${encodeURIComponent(type)}/${encodeURIComponent(slug)}`)
}

export async function submitContentFeedback(
  type: string,
  slug: string,
  isPositive: boolean
): Promise<{ status: string }> {
  return fetchAPI('/content/feedback', {
    method: 'POST',
    body: JSON.stringify({ content_type: type, content_slug: slug, is_positive: isPositive }),
  })
}

// =============================================================================
// What's New Discovery API
// =============================================================================

export interface WhatsNewResponse {
  unseen_releases: ReleaseNote[]
  unseen_count: number
  last_seen_version: string | null
  suppress_dialog: boolean
}

export async function getWhatsNew(): Promise<WhatsNewResponse> {
  return fetchAPI('/settings/whats-new')
}

export async function markWhatsNewSeen(version: string): Promise<{ status: string }> {
  return fetchAPI('/settings/whats-new/seen', {
    method: 'POST',
    body: JSON.stringify({ version }),
  })
}

export async function updateWhatsNewShowOnLogin(show: boolean): Promise<UserSettings> {
  return fetchAPI('/settings', {
    method: 'PATCH',
    body: JSON.stringify({ dashboard: { whats_new_show_on_login: show } }),
  })
}

// =============================================================================
// Analytics API
// =============================================================================

export interface PlatformStats {
  total_users: number
  total_apps: number
  running_apps: number
  deploy_success_rate: number  // compatibility alias for running_app_ratio
  running_app_ratio: number
  deploy_attempts: number
  apps_in_error: number
  active_users_daily: number
  active_users_weekly: number
  active_users_monthly: number
  period: string
}

export interface AppStats {
  app_name: string
  build_count: number
  deploy_count: number
  shared_with_count: number
  period: string
}

export interface UserBreakdown {
  user_id: number
  email: string
  name: string | null
  app_count: number
}

export interface AppBreakdown {
  app_name: string
  event_count: number
  owner_email: string
}

export interface AdminAnalytics {
  platform: PlatformStats
  users: UserBreakdown[]
  top_apps: AppBreakdown[]
}

export async function getPlatformStats(period: string = '30d'): Promise<PlatformStats> {
  return fetchAPI(`/analytics/platform?period=${period}`)
}

export async function getAppStats(appName: string, period: string = '30d'): Promise<AppStats> {
  return fetchAPI(`/analytics/apps/${encodeURIComponent(appName)}?period=${period}`)
}

export async function getAdminAnalytics(period: string = '30d'): Promise<AdminAnalytics> {
  return fetchAPI(`/analytics/admin?period=${period}`)
}

// Phase 46: Trend data, activity feed, realtime, feature adoption, app runtime

// visits aggregates platform_events.app_visit across the whole platform
// per day, split into three disjoint buckets:
//   visits        — signed-in visitor, NOT the app owner (external)
//   owner_visits  — signed-in visitor IS the app owner
//   anon_visits   — no session (public-access visitors)
// _prod-suffixed variants restrict to metadata.slot='prod'. The admin
// Apps chart toggles (Only published / Include owner / Show anonymous)
// pick between these without a re-fetch.
export interface TrendPoint {
  date: string
  active_users: number
  new_users: number
  total_apps: number
  deploys: number
  visits?: number
  visits_prod?: number
  unique_visitors?: number
  unique_visitors_prod?: number
  owner_visits?: number
  owner_visits_prod?: number
  unique_with_owner?: number
  unique_with_owner_prod?: number
  anon_visits?: number
}

export interface ActivityEvent {
  id: number
  event_type: string
  user_email: string | null
  user_name: string | null
  resource_type: string | null
  resource_id: string | null
  description: string
  created_at: string
}

export interface RealtimeSnapshot {
  running_apps: number
  active_workspaces: number
  connected_users: number
}

export interface FeatureUsage {
  feature: string
  distinct_users: number
  event_count: number
}

export interface FeatureAdoptionTrend {
  date: string
  feature: string
  event_count: number
}

export interface FeatureUserUsage {
  feature: string
  user_id: number
  user_email: string
  user_name: string | null
  event_count: number
  last_used_at: string | null
  action_breakdown?: Record<string, number>
}

export interface AppRuntimeBucket {
  bucket: string
  count: number
}

export interface AppLifecycleTrend {
  date: string
  action: string
  event_count: number
}

export async function getAnalyticsTrends(period: string = '30d'): Promise<TrendPoint[]> {
  return fetchAPI(`/analytics/admin/trends?period=${period}`)
}

export async function getActivityFeed(limit: number = 50): Promise<ActivityEvent[]> {
  return fetchAPI(`/analytics/admin/activity-feed?limit=${limit}`)
}

export async function getRealtimeSnapshot(): Promise<RealtimeSnapshot> {
  return fetchAPI(`/analytics/admin/realtime`)
}

export async function getFeatureAdoption(period: string = '30d'): Promise<FeatureUsage[]> {
  return fetchAPI(`/analytics/admin/feature-adoption?period=${period}`)
}

export async function getFeatureAdoptionTrends(period: string = '30d'): Promise<FeatureAdoptionTrend[]> {
  return fetchAPI(`/analytics/admin/feature-adoption-trends?period=${period}`)
}

export async function getFeatureAdoptionByUser(
  period: string = '30d',
  feature?: string,
  limitPerFeature: number = 20,
): Promise<FeatureUserUsage[]> {
  const params = new URLSearchParams({ period, limit_per_feature: String(limitPerFeature) })
  if (feature) params.set('feature', feature)
  return fetchAPI(`/analytics/admin/feature-adoption-by-user?${params.toString()}`)
}

export async function getAppRuntime(): Promise<AppRuntimeBucket[]> {
  return fetchAPI(`/analytics/admin/app-runtime`)
}

export async function getAppLifecycleTrends(period: string = '30d'): Promise<AppLifecycleTrend[]> {
  return fetchAPI(`/analytics/admin/app-lifecycle-trends?period=${period}`)
}

// =============================================================================
// Chatbot Analytics API (admin)
// =============================================================================

export interface ChatbotStats {
  total_conversations: number
  total_messages: number
  unique_users: number
  avg_messages_per_conversation: number
  escalation_count: number
  escalation_rate: number
  resolved_count: number
  unresolved_count: number
  resolution_rate: number
}

export interface ChatSessionSummary {
  id: number
  user_email: string
  user_name: string | null
  started_at: string
  last_message_at: string
  message_count: number
  escalated: boolean
  resolution: 'solved' | 'unresolved' | null
}

export interface ChatMessageEntry {
  role: string
  content: string
  created_at: string
}

export interface TopQuestion {
  question: string
  user_email: string
  date: string
  message_count: number
  escalated: boolean
}

export async function getChatbotStats(period = '30d'): Promise<ChatbotStats> {
  return fetchAPI(`/analytics/admin/chatbot?period=${period}`)
}

export async function getChatbotSessions(
  period = '30d',
  page = 1,
  escalatedOnly = false,
  perPage = 10,
  resolution?: 'solved' | 'unresolved'
): Promise<ChatSessionSummary[]> {
  const params = new URLSearchParams({
    period,
    page: String(page),
    per_page: String(perPage),
    escalated_only: String(escalatedOnly),
  })
  if (resolution) params.set('resolution', resolution)
  return fetchAPI(`/analytics/admin/chatbot/sessions?${params}`)
}

export async function getChatbotSessionMessages(sessionId: number): Promise<ChatMessageEntry[]> {
  return fetchAPI(`/analytics/admin/chatbot/sessions/${sessionId}/messages`)
}

export async function getChatbotTopQuestions(period = '30d'): Promise<TopQuestion[]> {
  return fetchAPI(`/analytics/admin/chatbot/top-questions?period=${period}`)
}

export interface ChatbotTheme {
  name: string
  description: string
  count: number
  escalation_rate: number
  example_question: string
  recommendation: string
}

export interface ChatbotInsights {
  themes: ChatbotTheme[]
  summary: string
  gaps: string[]
}

export async function generateChatbotInsights(period = '30d'): Promise<ChatbotInsights> {
  return fetchAPI(`/analytics/admin/chatbot/insights?period=${period}`, {
    method: 'POST',
  })
}

// =============================================================================
// Creator Analytics API
// =============================================================================

// visit_count_30d / unique_visitors_30d are EXTERNAL only (owner excluded)
// across ALL slots. owner_* fields surface the owner's own usage. The
// _prod-suffixed variants restrict to metadata.slot='prod' so the
// "Only published" toggle can flip the dashboard between "show traction
// across all environments" and "show real prod traffic only" without
// hitting the API again.
export interface CreatorAppStats {
  app_name: string
  visit_count_30d: number
  unique_visitors_30d: number
  owner_visit_count_30d: number
  owner_unique_visitors_30d: number
  visit_count_prod_30d: number
  unique_visitors_prod_30d: number
  owner_visit_count_prod_30d: number
  owner_unique_visitors_prod_30d: number
}

export interface VisitorTrendPoint {
  date: string
  visits: number
  unique_visitors: number
  owner_visits: number
  visits_prod: number
  unique_visitors_prod: number
  owner_visits_prod: number
}

export interface CreatorAppSummary {
  app_name: string
  display_name: string | null
  visit_count_30d: number
  unique_visitors_30d: number
  owner_visit_count_30d: number
  owner_unique_visitors_30d: number
  visit_count_prod_30d: number
  unique_visitors_prod_30d: number
  owner_visit_count_prod_30d: number
  owner_unique_visitors_prod_30d: number
}

export interface CreatorOverview {
  total_visitors_30d: number
  total_unique_visitors_30d: number
  total_apps: number
  most_visited_app: string | null
  most_visited_count: number
  wow_change: number
  this_week_visits: number
  last_week_visits: number
  app_stats: CreatorAppSummary[]
}

export async function getCreatorOverview(): Promise<CreatorOverview> {
  return fetchAPI('/analytics/creator/overview')
}

export async function getCreatorAppStats(appName: string): Promise<CreatorAppStats> {
  return fetchAPI(`/analytics/creator/apps/${encodeURIComponent(appName)}`)
}

export async function getCreatorAppTrends(appName: string): Promise<VisitorTrendPoint[]> {
  return fetchAPI(`/analytics/creator/apps/${encodeURIComponent(appName)}/trends`)
}

// Folders
export interface Folder {
  id: number
  name: string
  color: string
  context: string
  sort_order: number
  app_count: number
  created_at: string
  updated_at: string
}

export interface FolderApp {
  id: number
  name: string
  display_name: string | null
  description: string | null
  status: string | null
  added_at: string
}

export interface AddAppToFolderResult {
  status: string
  added: boolean
}

export interface RemoveAppFromFolderResult {
  status: string
  removed: boolean
}

export async function listFolders(context?: string): Promise<Folder[]> {
  const params = context ? `?context=${encodeURIComponent(context)}` : ''
  return fetchAPI(`/folders${params}`)
}

export async function createFolder(name: string, color: string = 'gray', context: string = 'my-apps'): Promise<Folder> {
  return fetchAPI('/folders', {
    method: 'POST',
    body: JSON.stringify({ name, color, context }),
  })
}

export async function updateFolder(id: number, data: { name?: string; color?: string }): Promise<Folder> {
  return fetchAPI(`/folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteFolder(id: number): Promise<{ status: string; apps_unfiled: number }> {
  return fetchAPI(`/folders/${id}`, { method: 'DELETE' })
}

export async function reorderFolders(folderIds: number[]): Promise<void> {
  return fetchAPI('/folders/reorder', {
    method: 'PUT',
    body: JSON.stringify({ folder_ids: folderIds }),
  })
}

export async function listFolderApps(folderId: number): Promise<FolderApp[]> {
  return fetchAPI(`/folders/${folderId}/apps`)
}

export async function addAppToFolder(folderId: number, appName: string): Promise<AddAppToFolderResult> {
  return fetchAPI(`/folders/${folderId}/apps/${encodeURIComponent(appName)}`, { method: 'POST' })
}

export async function removeAppFromFolder(folderId: number, appName: string): Promise<RemoveAppFromFolderResult> {
  return fetchAPI(`/folders/${folderId}/apps/${encodeURIComponent(appName)}`, { method: 'DELETE' })
}

// =============================================================================
// Notifications
// =============================================================================

// One entry from notifications.recent_events — the per-event history bundled
// into a grouped notification by ON CONFLICT DO UPDATE on the backend.
// The frontend renders one of these per sub-row when the user expands a
// grouped notification on the full /notifications page. `data` is per-event
// so each sub-row's deep-link resolves to the right target (different
// release versions, different child app names, etc.).
export interface NotificationEvent {
  at: string  // ISO timestamp
  title: string
  message: string
  data: Record<string, unknown>
}

export interface Notification {
  id: number
  type: string  // 'app_deployed', 'conflict_detected', 'app_shared', 'access_request', 'app_trending', 'collaborator_added', 'system_update'
  title: string
  message: string
  data: Record<string, unknown>
  is_read: boolean
  created_at: string
  priority?: string  // 'high' | 'medium' | 'low'
  group_count?: number
  // Rolling history (capped at 20 entries, newest-first) of the individual
  // events that got collapsed into this row. Empty for rows that pre-date
  // migration 073 — UI shows a graceful "no history" message in that case.
  recent_events?: NotificationEvent[]
}

export interface NotificationPreference {
  type: string
  enabled: boolean
}

export async function listNotifications(options?: { limit?: number, offset?: number, type?: string, sort?: string, unreadOnly?: boolean }): Promise<Notification[]> {
  const params = new URLSearchParams()
  if (options?.limit) params.set('limit', String(options.limit))
  if (options?.offset) params.set('offset', String(options.offset))
  if (options?.type) params.set('type', options.type)
  if (options?.sort) params.set('sort', options.sort)
  if (options?.unreadOnly) params.set('unread_only', 'true')
  const qs = params.toString()
  return fetchAPI(`/notifications${qs ? `?${qs}` : ''}`)
}

export async function getUnreadCount(): Promise<{ count: number }> {
  return fetchAPI('/notifications/unread-count')
}

export async function markNotificationRead(id: number): Promise<{ status: string }> {
  return fetchAPI(`/notifications/${id}/read`, { method: 'POST' })
}

export async function markAllNotificationsRead(): Promise<{ status: string }> {
  return fetchAPI('/notifications/read-all', { method: 'POST' })
}

export async function getNotificationPreferences(): Promise<NotificationPreference[]> {
  return fetchAPI('/notifications/preferences')
}

export async function updateNotificationPreferences(preferences: NotificationPreference[]): Promise<void> {
  await fetchAPI('/notifications/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preferences }),
  })
}

export async function bulkMarkRead(ids: number[]): Promise<void> {
  await fetchAPI('/notifications/bulk-read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
}

export async function bulkDeleteNotifications(ids: number[]): Promise<void> {
  await fetchAPI('/notifications/bulk-delete', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
}

export async function clearNotificationType(type: string): Promise<void> {
  await fetchAPI('/notifications/clear-type', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type }),
  })
}

export async function deleteNotification(id: number): Promise<void> {
  await fetchAPI(`/notifications/${id}`, { method: 'DELETE' })
}

// =============================================================================
// Push Subscriptions API
// =============================================================================

export async function getVapidPublicKey(): Promise<{ public_key: string }> {
  return fetchAPI('/push-subscriptions/vapid-key')
}

export async function subscribeToPush(subscription: {
  endpoint: string
  p256dh: string
  auth: string
}): Promise<{ status: string }> {
  return fetchAPI('/push-subscriptions', {
    method: 'POST',
    body: JSON.stringify(subscription),
  })
}

export async function unsubscribeFromPush(endpoint: string): Promise<{ status: string }> {
  return fetchAPI('/push-subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  })
}

export interface PushSubscriptionItem {
  endpoint: string
  created_at: string
}

export async function listPushSubscriptions(): Promise<PushSubscriptionItem[]> {
  return fetchAPI('/push-subscriptions')
}

// =============================================================================
// User Activity Feed
// =============================================================================

export interface ActivityEvent {
  id: number
  event_type: string
  user_email: string | null
  user_name: string | null
  resource_type: string | null
  resource_id: string | null
  description: string
  created_at: string
}

export async function getMyActivity(limit = 20): Promise<ActivityEvent[]> {
  return fetchAPI(`/analytics/my-activity?limit=${limit}`)
}

// =============================================================================
// Templates (Phase 67 — templates are deployed_apps with is_template flag)
// =============================================================================

export interface TemplatePreviewImage {
  id: number
  app_id: number
  image_url: string
  sort_order: number
  is_hero: boolean
  created_at: string
}

// Template Admin API

export async function listTemplates(): Promise<App[]> {
  return fetchAPI('/templates')
}

export async function updateTemplate(name: string, data: {
  display_name?: string
  template_one_liner?: string
  template_description?: string
}): Promise<App> {
  return fetchAPI(`/templates/${name}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function setTemplateHeroImage(name: string, imageId: number): Promise<void> {
  return fetchAPI(`/templates/${name}/images/${imageId}/hero`, { method: 'PATCH' })
}

export async function activateTemplate(name: string): Promise<void> {
  return fetchAPI(`/templates/${name}/activate`, { method: 'PATCH' })
}

export async function deactivateTemplate(name: string): Promise<void> {
  return fetchAPI(`/templates/${name}/deactivate`, { method: 'PATCH' })
}

export async function promoteToTemplate(appName: string): Promise<App> {
  return fetchAPI(`/templates/promote/${appName}`, { method: 'POST' })
}

export async function listTemplateImages(name: string): Promise<TemplatePreviewImage[]> {
  return fetchAPI(`/templates/${name}/images`)
}

export async function removeTemplateImage(name: string, imageId: number): Promise<void> {
  return fetchAPI(`/templates/${name}/images/${imageId}`, { method: 'DELETE' })
}

export async function uploadTemplateImages(name: string, files: FileList | File[]): Promise<TemplatePreviewImage[]> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  const response = await fetch(`${API_BASE}/templates/${name}/images/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  })
  if (!response.ok) {
    const text = await response.text()
    let message = `Upload failed (${response.status})`
    try { message = JSON.parse(text)?.error?.message || JSON.parse(text)?.detail || message } catch { /* use default */ }
    throw new Error(message)
  }
  return response.json()
}

// Template Gallery API (user-facing)

export async function deleteTemplate(name: string): Promise<void> {
  return fetchAPI(`/templates/${name}`, { method: 'DELETE' })
}

export async function reorderTemplateImages(name: string, imageIds: number[]): Promise<void> {
  return fetchAPI(`/templates/${name}/images/reorder`, {
    method: 'PATCH',
    body: JSON.stringify({ image_ids: imageIds }),
  })
}

export interface GalleryTemplateDetail extends App {
  preview_images: TemplatePreviewImage[]
}

export async function getGalleryTemplates(): Promise<App[]> {
  return fetchAPI('/templates/gallery')
}

export async function getGalleryTemplate(name: string): Promise<GalleryTemplateDetail> {
  return fetchAPI(`/templates/gallery/${name}`)
}

export async function createFromTemplate(name: string, appName: string, displayName?: string): Promise<App> {
  return fetchAPI(`/templates/${name}/use`, {
    method: 'POST',
    body: JSON.stringify({ name: appName, display_name: displayName }),
  })
}

// Template Analytics API (admin-only)

export interface TemplateAnalytics {
  template_id: number
  template_name: string
  template_display_name: string | null
  template_slug: string
  is_active: boolean
  total_apps_created: number
  active_apps: number
  stopped_apps: number
  deleted_apps: number
  last_used: string | null
  created_at: string
}

export interface DerivedApp {
  name: string
  display_name: string | null
  owner_email: string | null
  status: string
  created_at: string
}

export async function getTemplateAnalytics(): Promise<TemplateAnalytics[]> {
  return fetchAPI('/templates/analytics')
}

export async function getTemplateDerivedApps(name: string): Promise<DerivedApp[]> {
  return fetchAPI(`/templates/${name}/derived-apps`)
}

export async function recordChangelogVisit(
  slug: string,
  options?: { source?: string; referrer?: string | null },
): Promise<void> {
  await fetchAPI('/content/changelog-visits', {
    method: 'POST',
    body: JSON.stringify({
      slug,
      source: options?.source,
      referrer: options?.referrer,
    }),
  })
}

export interface ChangelogViewer {
  user_id: number
  email: string
  name: string | null
  is_admin: boolean
  last_visited_at: string
  visit_count: number
  last_source: string | null
}

export interface ChangelogSourceBreakdown {
  source: string
  visits: number
  unique_users: number
}

export interface ChangelogVisitAggregate {
  slug: string
  total_visits: number
  unique_users: number
  viewers: ChangelogViewer[]
  sources: ChangelogSourceBreakdown[]
}

export async function getChangelogVisitsAdmin(): Promise<ChangelogVisitAggregate[]> {
  return fetchAPI('/admin/changelog-visits')
}

export interface ChangelogVisitBucket {
  date: string
  visits: number
}

export interface ChangelogVisitTrends {
  period_days: number
  bucket: 'day' | 'week'
  buckets: ChangelogVisitBucket[]
  by_release: { slug: string; visits: number }[]
}

export async function getChangelogVisitTrendsAdmin(
  period: string,
  slug?: string,
): Promise<ChangelogVisitTrends> {
  const params = new URLSearchParams({ period })
  if (slug) params.set('slug', slug)
  return fetchAPI(`/admin/changelog-visits/trends?${params.toString()}`)
}

// -- Phase 98 ownership-transfer wrappers --
// Backend contract (Plan 02): POST/GET/DELETE /apps/{name}/transfer,
// GET /apps/transfers/incoming, POST /apps/transfers/{id}/accept,
// POST /apps/transfers/{id}/decline. Responses use manual camelCase keys
// (matches existing share-endpoint style at apps.py:3300+).

export type TransferStatus = 'pending' | 'accepted' | 'declined' | 'cancelled'

export interface TransferPending {
  transferId: number
  toEmail: string
  status: TransferStatus
  initiatedAt: string
}

export interface IncomingTransfer {
  transferId: number
  appName: string
  appDisplayName: string | null
  fromEmail: string | null
  initiatedAt: string
}

export async function initiateTransfer(
  appName: string,
  toEmail: string,
): Promise<TransferPending & { appName: string }> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ to_email: toEmail }),
  })
}

export async function getPendingTransfer(
  appName: string,
): Promise<{ pending: TransferPending | null }> {
  return fetchAPI(`/apps/${encodeURIComponent(appName)}/transfer`)
}

export async function cancelTransfer(appName: string): Promise<void> {
  await fetchAPI(`/apps/${encodeURIComponent(appName)}/transfer`, {
    method: 'DELETE',
  })
}

export async function listIncomingTransfers(): Promise<{ incoming: IncomingTransfer[] }> {
  return fetchAPI('/apps/transfers/incoming')
}

export async function acceptTransfer(
  transferId: number,
): Promise<{ transferId: number; appName: string; status: 'accepted'; newOwnerEmail: string }> {
  return fetchAPI(`/apps/transfers/${transferId}/accept`, { method: 'POST' })
}

export async function declineTransfer(
  transferId: number,
): Promise<{ transferId: number; status: 'declined' }> {
  return fetchAPI(`/apps/transfers/${transferId}/decline`, { method: 'POST' })
}
